import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { automationService } from '../services/automationService';
import { automationStepsToCanvasSteps, canvasStepsToAutomationSteps } from '../services/automationStepNormalization';
import {
  automationTemplateService,
  AutomationTemplate
} from '../services/automationTemplateService';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNotification } from '../contexts/NotificationContext';
import TriggerFilterBuilder from './automation/TriggerFilterBuilder';
import EmailTemplateSelector from './EmailTemplateSelector';
import SimpleEmailActionModal, { EmailActionConfig } from './SimpleEmailActionModal';
import { TriggerFilter, ADDED_TO_CART_FIELDS, FilterFieldDefinition } from '../types/triggerFilters';
import { describeFilter } from '../services/triggerFilterEvaluator';
import { getEmailTemplate, EmailTemplate } from '../services/emailTemplates';
import { secureEmailService } from '../services/secureEmailService';
import { integrationService } from '../services/integrationService';
import { sendingDomainService, SendingDomain } from '../services/sendingDomainService';
import {
  StarIcon,
  UserPlusIcon,
  EnvelopeOpenIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  LinkIcon,
  UserGroupIcon,
  PencilSquareIcon,
  TagIcon,
  XMarkIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  CreditCardIcon,
  EyeIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ClockIcon,
  PlayIcon,
  CodeBracketIcon,
  RocketLaunchIcon,
  GiftIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import AutomationActivityLog from './automation/AutomationActivityLog';

export type TriggerCategoryOption = {
  value: string;
  label: string;
  description: string;
  icon?: React.ReactNode;
  supportsFilters?: boolean;
  requiresStoreConnection?: boolean;
};

type TriggerCategory = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  options: TriggerCategoryOption[];
};

const TRIGGER_CATEGORIES: TriggerCategory[] = [
  {
    id: 'popular',
    title: 'Popular',
    icon: <StarIcon className="w-5 h-5" />,
    description: 'The most frequently used triggers to get you started quickly.',
    options: [
      { value: 'contact_added', label: 'Contact is created', description: 'Launch the workflow whenever a new contact joins your workspace.', icon: <UserPlusIcon className="w-6 h-6" /> },
      { value: 'email_opened', label: 'Email is opened', description: 'Follow up when recipients open one of your emails.', icon: <EnvelopeOpenIcon className="w-6 h-6" /> },
      { value: 'birthday', label: 'Birthday', description: 'Send personalized messages when a contact celebrates their birthday.', icon: <GiftIcon className="w-6 h-6" /> },
      { value: 'added_product_to_cart', label: 'Product added to cart', description: 'React as soon as a product is dropped into the shopping cart.', supportsFilters: true, requiresStoreConnection: true, icon: <ShoppingCartIcon className="w-6 h-6" /> },
      { value: 'abandoned_cart', label: 'Cart is abandoned', description: 'Recover revenue automatically when carts are left behind.', supportsFilters: true, requiresStoreConnection: true, icon: <ShoppingBagIcon className="w-6 h-6" /> },
      { value: 'placed_order', label: 'Order is placed', description: 'Continue the journey right after a customer completes an order.', supportsFilters: true, requiresStoreConnection: true, icon: <CheckCircleIcon className="w-6 h-6" /> },
      { value: 'email_clicked', label: 'Email link is clicked', description: 'Continue the journey when someone clicks a link in your email.', icon: <LinkIcon className="w-6 h-6" /> },
    ],
  },
  {
    id: 'contact',
    title: 'Contact activity',
    icon: <UserGroupIcon className="w-5 h-5" />,
    description: 'Start journeys when something changes on a contact record.',
    options: [
      { value: 'contact_added', label: 'Contact is created', description: 'Launch the workflow whenever a new contact joins your workspace.', icon: <UserPlusIcon className="w-6 h-6" /> },
      { value: 'contact_updated', label: 'Contact is updated', description: 'Run whenever an existing contact profile data changes.', icon: <PencilSquareIcon className="w-6 h-6" /> },
      { value: 'contact_tag_added', label: 'Tag is added', description: 'Trigger when a specific tag is applied to a contact.', icon: <TagIcon className="w-6 h-6" /> },
      { value: 'contact_tag_removed', label: 'Tag is removed', description: 'Start when a tag is removed from a contact.', icon: <XMarkIcon className="w-6 h-6" /> },
    ],
  },
  {
    id: 'email',
    title: 'Email engagement',
    icon: <EnvelopeIcon className="w-5 h-5" />,
    description: 'React to how people interact with the campaigns you send.',
    options: [
      { value: 'email_opened', label: 'Email is opened', description: 'Follow up when recipients open one of your emails.', icon: <EnvelopeOpenIcon className="w-6 h-6" /> },
      { value: 'email_clicked', label: 'Email link is clicked', description: 'Continue the journey when someone clicks a link in your email.', icon: <LinkIcon className="w-6 h-6" /> },
      { value: 'email_bounced', label: 'Email bounces', description: 'Handle bounce events automatically with a dedicated path.', icon: <ArrowPathIcon className="w-6 h-6" /> },
      { value: 'email_unsubscribed', label: 'Contact unsubscribes', description: 'Respond when a contact unsubscribes from your messages.', icon: <XMarkIcon className="w-6 h-6" /> },
    ],
  },
  {
    id: 'commerce',
    title: 'Commerce events',
    icon: <ShoppingBagIcon className="w-5 h-5" />,
    description: 'Keep the conversation going around carts, orders, and browsing.',
    options: [
      { value: 'added_product_to_cart', label: 'Product added to cart', description: 'React as soon as a product is dropped into the shopping cart.', supportsFilters: true, requiresStoreConnection: true, icon: <ShoppingCartIcon className="w-6 h-6" /> },
      { value: 'started_checkout', label: 'Checkout started', description: 'Engage visitors the moment they begin checkout.', supportsFilters: true, requiresStoreConnection: true, icon: <CreditCardIcon className="w-6 h-6" /> },
      { value: 'abandoned_cart', label: 'Cart is abandoned', description: 'Recover revenue automatically when carts are left behind.', supportsFilters: true, requiresStoreConnection: true, icon: <ShoppingBagIcon className="w-6 h-6" /> },
      { value: 'placed_order', label: 'Order is placed', description: 'Continue the journey right after a customer completes an order.', supportsFilters: true, requiresStoreConnection: true, icon: <CheckCircleIcon className="w-6 h-6" /> },
      { value: 'viewed_product', label: 'Product is viewed', description: 'Follow up when someone browses a product page.', supportsFilters: true, requiresStoreConnection: true, icon: <EyeIcon className="w-6 h-6" /> },
    ],
  },
  {
    id: 'schedule',
    title: 'Schedule & milestones',
    icon: <CalendarIcon className="w-5 h-5" />,
    description: 'Start workflows based on important dates and anniversaries.',
    options: [
      { value: 'date_trigger', label: 'Specific date', description: 'Launch this automation on a particular date and time.', icon: <CalendarDaysIcon className="w-6 h-6" /> },
      { value: 'anniversary', label: 'Recurring anniversary', description: 'Run every year for birthdays, renewal reminders, or other milestones.', icon: <ClockIcon className="w-6 h-6" /> },
    ],
  },
  {
    id: 'manual',
    title: 'Manual & API',
    icon: <RocketLaunchIcon className="w-5 h-5" />,
    description: 'Trigger automations yourself or from your own systems.',
    options: [
      { value: 'manual', label: 'Manual trigger', description: 'Kick things off manually from Scubamail whenever you need to.', icon: <PlayIcon className="w-6 h-6" /> },
      { value: 'api_trigger', label: 'API trigger', description: 'Start the automation via the Scubamail API from your product.', icon: <CodeBracketIcon className="w-6 h-6" /> },
    ],
  },
];

const SPLIT_AVAILABLE_FIELDS: FilterFieldDefinition[] = [
  {
    path: 'contact.email',
    label: 'Email',
    type: 'string',
    category: 'Contact details',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with', 'exists', 'does_not_exist']
  },
  {
    path: 'contact.firstName',
    label: 'First name',
    type: 'string',
    category: 'Contact details',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'contact.lastName',
    label: 'Last name',
    type: 'string',
    category: 'Contact details',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'contact.country',
    label: 'Country',
    type: 'string',
    category: 'Location',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'contact.state',
    label: 'State/Province',
    type: 'string',
    category: 'Location',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'contact.city',
    label: 'City',
    type: 'string',
    category: 'Location',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  },
  {
    path: 'contact.tags',
    label: 'Tags',
    type: 'array',
    category: 'Segmentation',
    supportedOperators: ['contains', 'does_not_contain', 'in', 'not_in', 'exists', 'does_not_exist']
  },
  {
    path: 'contact.segments',
    label: 'Segments',
    type: 'array',
    category: 'Segmentation',
    supportedOperators: ['contains', 'does_not_contain', 'in', 'not_in', 'exists', 'does_not_exist']
  },
  {
    path: 'contact.createdAt',
    label: 'Created date',
    type: 'date',
    category: 'Lifecycle',
    supportedOperators: ['greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'between', 'exists', 'does_not_exist']
  },
  {
    path: 'contact.attributes.lifetimeValue',
    label: 'Lifetime value',
    type: 'number',
    category: 'Lifecycle',
    supportedOperators: ['greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'between']
  },
  {
    path: 'trigger.eventType',
    label: 'Last trigger event',
    type: 'string',
    category: 'Trigger context',
    supportedOperators: ['is', 'is_not', 'contains', 'does_not_contain', 'starts_with', 'ends_with']
  }
];

const TRIGGER_EVENT_DETAILS = TRIGGER_CATEGORIES.reduce<Record<string, TriggerCategoryOption & { categoryId: string; categoryTitle: string }>>(
  (acc, category) => {
    category.options.forEach(option => {
      acc[option.value] = { ...option, categoryId: category.id, categoryTitle: category.title };
    });
    return acc;
  },
  {}
);

const TRIGGER_EVENTS = TRIGGER_CATEGORIES.flatMap(category =>
  category.options.map(option => ({ value: option.value, label: option.label }))
);

const TRIGGER_TITLE_PHRASES: Record<string, string> = {
  contact_added: 'a contact joins your audience',
  contact_updated: 'a contact profile is updated',
  contact_tag_added: 'a tag is added to a contact',
  contact_tag_removed: 'a tag is removed from a contact',
  email_opened: 'someone opens one of your emails',
  email_clicked: 'someone clicks a link in your email',
  email_bounced: 'an email bounces',
  email_unsubscribed: 'a contact unsubscribes from your emails',
  added_product_to_cart: 'a customer adds a product to the cart',
  started_checkout: 'a customer starts the checkout process',
  abandoned_cart: 'a cart is abandoned',
  placed_order: 'a customer places an order',
  viewed_product: 'a customer views a product',
  date_trigger: 'a scheduled date arrives',
  anniversary: 'a recurring date or milestone happens',
  manual: 'you start it manually',
  api_trigger: 'your system triggers it via API',
};

interface WorkflowElement {
  id: string;
  type: 'trigger' | 'email' | 'sms' | 'push' | 'delay' | 'tag' | 'split' | 'ab_test' | 'exit';
  title: string;
  description?: string;
  x: number;
  y: number;
  connections: string[];
  config: any;
}

interface DragItem {
  type: string;
  title: string;
  icon: React.ReactNode;
}

/** Fresh trigger + exit canvas (no delay/email). Used as default and when a saved doc has no steps yet. */
const createMinimalWorkflowElements = (): WorkflowElement[] => [
  {
    id: 'trigger-1',
    type: 'trigger',
    title: 'Define how a person enters a workflow',
    x: 100,
    y: 50,
    connections: [],
    config: {}
  },
  {
    id: 'exit-1',
    type: 'exit',
    title: 'Exit',
    x: 100,
    y: 300,
    connections: [],
    config: {}
  }
];

const AutomationEditor: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId, automationId: paramAutomationId } = useParams<{ workspaceId?: string; automationId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const automationId = paramAutomationId || searchParams.get('automationId') || undefined;
  /** Workspace list filters by workspaceId; URL loses it on /automations/edit/:id, so keep id from loaded doc. */
  const [loadedAutomationWorkspaceId, setLoadedAutomationWorkspaceId] = useState<string | undefined>();
  const effectiveWorkspaceId = workspaceId ?? loadedAutomationWorkspaceId;

  useEffect(() => {
    if (!automationId) {
      setLoadedAutomationWorkspaceId(undefined);
      setAutomationStatus('draft');
    }
  }, [automationId]);

  // Template mode detection
  const isTemplateMode = searchParams.get('mode') === 'template';
  const templateId = searchParams.get('templateId') || undefined;
  const returnPath = searchParams.get('return') || '/automations';
  const isAdminTemplateWorkflow = isTemplateMode && returnPath.includes('/admin/automation-template-editor');

  const withAutomationIdInReturnPath = (basePath: string, id?: string) => {
    if (!id) return basePath;
    const [pathPart, queryString = ''] = basePath.split('?');
    const params = new URLSearchParams(queryString);
    if (!params.get('automationId')) {
      params.set('automationId', id);
    }
    const nextQuery = params.toString();
    return nextQuery ? `${pathPart}?${nextQuery}` : pathPart;
  };

  const { currentUser } = useAuth();
  const { activeUserId } = useWorkspace();
  // Sending domains belong to the account being viewed, which is the
  // impersonated user when an admin has entered a profile.
  const ownerId = activeUserId || currentUser?.uid;
  const { showError } = useNotification();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [automationName, setAutomationName] = useState(isTemplateMode ? 'New Template' : 'New Automation');
  const [automationStatus, setAutomationStatus] = useState<'active' | 'paused' | 'draft' | 'archived'>('draft');
  const [autoPauseReason, setAutoPauseReason] = useState<string | null>(null);
  const [showTemplateMetadataModal, setShowTemplateMetadataModal] = useState(false);
  const [showTriggerSelectionModal, setShowTriggerSelectionModal] = useState(false);
  const [activeTriggerCategoryId, setActiveTriggerCategoryId] = useState<string>(TRIGGER_CATEGORIES[0]?.id ?? '');

  const [workflowElements, setWorkflowElements] = useState<WorkflowElement[]>(() => createMinimalWorkflowElements());

  const [selectedElement, setSelectedElement] = useState<WorkflowElement | null>(null);
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ elementId: string; x: number; y: number } | null>(null);
  const [dropHint, setDropHint] = useState<{ x: number; y: number } | null>(null);
  // Right-panel (Delay) local state
  const [delayAmount, setDelayAmount] = useState<number>(1);
  const [delayUnit, setDelayUnit] = useState<'minutes' | 'hours' | 'days' | 'weeks'>('minutes');
  const [delaySelectedDaysOnly, setDelaySelectedDaysOnly] = useState<boolean>(false);
  // Right-panel (Trigger) local state
  const [triggerEvent, setTriggerEvent] = useState<string>('');
  const [triggerSkipAlreadyIn, setTriggerSkipAlreadyIn] = useState<boolean>(true);
  const [triggerWindowText, setTriggerWindowText] = useState<string>('in the last 1 day');
  const [triggerSkipOtherAutomations, setTriggerSkipOtherAutomations] = useState<boolean>(false);
  // Trigger - Contact filter local state
  const [contactFilterType, setContactFilterType] = useState<string>('');
  const [contactFilterValue, setContactFilterValue] = useState<string>('');
  const [contactFilterNumber, setContactFilterNumber] = useState<number>(30);
  const [contactFilterGender, setContactFilterGender] = useState<string>('');
  // Trigger - Trigger filters (e-commerce events, etc.)
  const [triggerFilters, setTriggerFilters] = useState<TriggerFilter[]>([]);
  // Trigger - Exit conditions
  type ExitCondition = { id: string; event: string };
  const [exitConditions, setExitConditions] = useState<ExitCondition[]>([]);
  // Right-panel (Email) local state
  const [emailName, setEmailName] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailFromName, setEmailFromName] = useState<string>('');
  const [emailFromEmail, setEmailFromEmail] = useState<string>('');
  const [emailReplyTo, setEmailReplyTo] = useState<string>('');
  const [emailPreheader, setEmailPreheader] = useState<string>('');
  const [emailUtmTracking, setEmailUtmTracking] = useState<boolean>(false);
  const [emailTemplateId, setEmailTemplateId] = useState<string>('');
  const [emailTemplateName, setEmailTemplateName] = useState<string>('');
  const [emailSendMode, setEmailSendMode] = useState<'immediate' | 'delay' | 'specific_time'>('immediate');
  const [emailDelayAmount, setEmailDelayAmount] = useState<number>(0);
  const [emailDelayUnit, setEmailDelayUnit] = useState<'minutes' | 'hours' | 'days'>('hours');
  const [emailSendTime, setEmailSendTime] = useState<string>('09:00');
  const [emailSendTimezone, setEmailSendTimezone] = useState<string>(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || 'UTC';
    } catch {
      return 'UTC';
    }
  });
  const [emailTrackOpens, setEmailTrackOpens] = useState<boolean>(true);
  const [emailTrackClicks, setEmailTrackClicks] = useState<boolean>(true);
  const [emailConditionalSendEnabled, setEmailConditionalSendEnabled] = useState<boolean>(false);
  const [emailConditionalTag, setEmailConditionalTag] = useState<string>('');
  const [showEmailEditorModal, setShowEmailEditorModal] = useState<boolean>(false);
  const [emailTemplateModalOpen, setEmailTemplateModalOpen] = useState<boolean>(false);
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState<boolean>(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
  const [emailPreviewLoading, setEmailPreviewLoading] = useState<boolean>(false);
  const [emailTestSending, setEmailTestSending] = useState<boolean>(false);
  const [emailSendingDomains, setEmailSendingDomains] = useState<SendingDomain[]>([]);
  const [emailFromEmailOptions, setEmailFromEmailOptions] = useState<Array<{ label: string; value: string; status: 'verified' | 'active' | 'pending' | 'failed' | 'default' | 'warming'; domainId?: string }>>([]);
  const [emailFromEmailStatus, setEmailFromEmailStatus] = useState<'verified' | 'active' | 'pending' | 'failed' | 'default' | 'unknown' | 'warming'>('default');
  const [emailTemplateMissing, setEmailTemplateMissing] = useState<boolean>(false);
  const [emailABTestEnabled, setEmailABTestEnabled] = useState<boolean>(false);
  const [emailABSubjectB, setEmailABSubjectB] = useState<string>('');
  const [emailABSplit, setEmailABSplit] = useState<number>(50);
  const [emailAnalyticsPreview, setEmailAnalyticsPreview] = useState<{ projectedOpens?: number; projectedClicks?: number; sampleSize?: number } | null>(null);
  // Right-panel (Split) local state
  const [splitFilters, setSplitFilters] = useState<TriggerFilter[]>([]);
  const [splitYesPath, setSplitYesPath] = useState<string>('__end__');
  const [splitNoPath, setSplitNoPath] = useState<string>('__end__');

  // Workflow lifecycle
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [editorView, setEditorView] = useState<'canvas' | 'activity'>('canvas');
  // Live execution summary for the Activity badge in the top bar
  const [executions, setExecutions] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // E-commerce store connection status
  const [hasConnectedStore, setHasConnectedStore] = useState<boolean>(false);

  // Sync isRunning state with automationStatus when automation loads
  useEffect(() => {
    if (automationStatus === 'active') {
      setIsRunning(true);
    } else {
      setIsRunning(false);
    }
  }, [automationStatus]);
  const storageKey = `automation:workflow:${automationId || effectiveWorkspaceId || workspaceId || 'default'}`;
  const availableTimezones = useMemo<string[]>(() => {
    const fallback = ['UTC', 'America/New_York', 'Europe/London', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney'];
    try {
      const intlAny = Intl as any;
      if (typeof intlAny?.supportedValuesOf === 'function') {
        const zones = intlAny.supportedValuesOf('timeZone');
        if (Array.isArray(zones) && zones.length > 0) {
          return zones;
        }
      }
    } catch {
      // ignore and use fallback
    }
    return fallback;
  }, []);
  const personalizationTokens = useMemo(
    () => [
      { label: 'First name', value: '{{first_name}}' },
      { label: 'Last name', value: '{{last_name}}' },
      { label: 'Full name', value: '{{name}}' },
      { label: 'Email', value: '{{email}}' },
      { label: 'Company', value: '{{company}}' },
      { label: 'City', value: '{{city}}' },
      { label: 'Country', value: '{{country}}' },
      { label: 'Phone', value: '{{phone}}' },
    ],
    []
  );
  const evaluateFromEmailStatus = useCallback((value: string, domains: SendingDomain[]) => {
    if (!value) {
      setEmailFromEmailStatus('default');
      return;
    }
    const normalized = value.toLowerCase();
    if (!normalized.includes('@')) {
      setEmailFromEmailStatus('unknown');
      return;
    }
    const [, domainPart] = normalized.split('@');
    const matchedDomain = domains.find(domain => domain.domain === domainPart);
    if (matchedDomain) {
      if (matchedDomain.status === 'verified') {
        setEmailFromEmailStatus('verified');
        return;
      }
      if (matchedDomain.status === 'active') {
        setEmailFromEmailStatus('active');
        return;
      }
      if (matchedDomain.status === 'pending') {
        setEmailFromEmailStatus('pending');
        return;
      }
      if (matchedDomain.status === 'failed') {
        setEmailFromEmailStatus('failed');
        return;
      }
    }
    if (normalized.endsWith('@scubamail.io') || normalized.endsWith('@scaledots.com')) {
      setEmailFromEmailStatus('default');
      return;
    }
    setEmailFromEmailStatus('unknown');
  }, []);
  const branchTargets = useMemo(() => {
    if (!selectedElement) return [];
    return workflowElements
      .filter(el => el.id !== selectedElement.id && el.type !== 'trigger')
      .sort((a, b) => a.y - b.y);
  }, [selectedElement, workflowElements]);

  // Check for connected e-commerce integrations
  useEffect(() => {
    const checkConnectedStore = async () => {
      if (!currentUser?.uid || !effectiveWorkspaceId) return;

      try {
        const integrations = await integrationService.getUserIntegrations(currentUser.uid, effectiveWorkspaceId);
        // Check if any e-commerce integration (Shopify, WooCommerce) is connected
        const ecommerceIntegration = integrations.find(
          int => (int.slug === 'shopify' || int.slug === 'woocommerce') && int.status === 'connected'
        );
        setHasConnectedStore(!!ecommerceIntegration);
      } catch (error) {
        console.error('Error checking connected integrations:', error);
        setHasConnectedStore(false);
      }
    };

    checkConnectedStore();
  }, [currentUser?.uid, effectiveWorkspaceId]);

  // Load existing automation or template when editing
  useEffect(() => {
    const loadAutomation = async () => {
      if (automationId) {
        setLoading(true);
        try {
          const automation = await automationService.getAutomation(automationId);
          if (automation) {
            setLoadedAutomationWorkspaceId(automation.workspaceId || undefined);
            setAutomationName(automation.name || 'Untitled Automation');
            setAutomationStatus((automation.status as 'active' | 'paused' | 'draft' | 'archived') || 'draft');
            setAutoPauseReason((automation as any).autoPauseReason || null);
            // Convert automation steps to workflow elements
            if (automation.steps && automation.steps.length > 0) {
              const canvasRows = automationStepsToCanvasSteps(automation.steps as unknown[]);
              const convertedElements: WorkflowElement[] = canvasRows.map((row) => ({
                id: row.id,
                type: row.type as WorkflowElement['type'],
                title: row.title || row.type,
                description: row.description,
                x: row.position?.x ?? 100,
                y: row.position?.y ?? 50,
                connections: row.connections || [],
                config: (row.config || {}) as any,
              }));

              // Add exit element if not present
              const hasExit = convertedElements.some(el => el.type === 'exit');
              if (!hasExit) {
                convertedElements.push({
                  id: 'exit-1',
                  type: 'exit',
                  title: 'Exit',
                  x: 100,
                  y: 300, // Default position, will be updated by autoLayoutNodes
                  connections: [],
                  config: {}
                });
              }

              // Apply auto-layout to ensure clean vertical alignment
              setWorkflowElements(autoLayoutNodes(convertedElements));
            } else {
              // Empty steps in Firestore: do not keep stale localStorage draft (would show ghost delay/email steps).
              setWorkflowElements(updateExitPosition(createMinimalWorkflowElements()));
              try {
                localStorage.removeItem(storageKey);
              } catch (_) { }
            }
          } else {
            setLoadedAutomationWorkspaceId(undefined);
            setAutomationStatus('draft');
          }
        } catch (error) {
          console.error('Error loading automation:', error);
          showError('Failed to load automation');
        } finally {
          setLoading(false);
        }
      } else if (isTemplateMode && templateId) {
        // Load template for editing
        setLoading(true);
        try {
          const template = await automationTemplateService.getTemplate(templateId);
          if (template) {
            setAutomationName(template.name || 'Untitled Template');
            // Convert template steps to workflow elements
            if (template.steps && template.steps.length > 0) {
              const canvasRows = automationStepsToCanvasSteps(template.steps as unknown[]);
              const convertedElements: WorkflowElement[] = canvasRows.map((row) => ({
                id: row.id,
                type: row.type as WorkflowElement['type'],
                title: row.title || row.type,
                description: row.description,
                x: row.position?.x ?? 100,
                y: row.position?.y ?? 50,
                connections: row.connections || [],
                config: (row.config || {}) as any,
              }));

              // Add exit element if not present
              const hasExit = convertedElements.some(el => el.type === 'exit');
              if (!hasExit) {
                convertedElements.push({
                  id: 'exit-1',
                  type: 'exit',
                  title: 'Exit',
                  x: 100,
                  y: 300, // Default position, will be updated by autoLayoutNodes
                  connections: [],
                  config: {}
                });
              }

              // Apply auto-layout to ensure clean vertical alignment
              setWorkflowElements(autoLayoutNodes(convertedElements));
            } else {
              setWorkflowElements(updateExitPosition(createMinimalWorkflowElements()));
              try {
                localStorage.removeItem(storageKey);
              } catch (_) { }
            }
          }
        } catch (error) {
          console.error('Error loading template:', error);
          showError('Failed to load template');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAutomation();
  }, [automationId, isTemplateMode, templateId, storageKey]);

  useEffect(() => {
    if (searchParams.get('showTriggerModal') === '1') {
      setShowTriggerSelectionModal(true);
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete('showTriggerModal');
      setSearchParams(updatedParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (showTriggerSelectionModal) {
      const currentCategoryId = triggerEvent ? TRIGGER_EVENT_DETAILS[triggerEvent]?.categoryId : undefined;
      setActiveTriggerCategoryId(currentCategoryId || TRIGGER_CATEGORIES[0]?.id || '');
    }
  }, [showTriggerSelectionModal, triggerEvent]);


  // Populate trigger configuration when workflow elements are loaded
  useEffect(() => {
    const triggerElement = workflowElements.find(el => el.type === 'trigger');
    if (triggerElement && triggerElement.config) {
      // Check if config has type property (from templates)
      if (triggerElement.config.type) {
        setTriggerEvent(triggerElement.config.type);
      }
      // Load event from config
      if (triggerElement.config.event) {
        setTriggerEvent(triggerElement.config.event);
      }
      // Load trigger filters from config
      if (Array.isArray(triggerElement.config.triggerFilters)) {
        setTriggerFilters(triggerElement.config.triggerFilters);
      }
      // Load other trigger config
      if (triggerElement.config.skipAlreadyIn !== undefined) {
        setTriggerSkipAlreadyIn(triggerElement.config.skipAlreadyIn);
      }
      if (triggerElement.config.windowText) {
        setTriggerWindowText(triggerElement.config.windowText);
      }
      // Also set selectedElement to the trigger if none is selected
      if (!selectedElement) {
        setSelectedElement(triggerElement);
      }
    }
  }, [workflowElements]);

  // A/B Test local state
  const [abTrafficA, setAbTrafficA] = useState<number>(50);
  const [abTrafficB, setAbTrafficB] = useState<number>(50);
  const [abGoal, setAbGoal] = useState<string>('open_rate');
  const [abWindowHours, setAbWindowHours] = useState<number>(24);
  const [abAutoWinner, setAbAutoWinner] = useState<boolean>(true);

  const EXIT_EVENTS: { value: string; label: string }[] = [
    { value: 'added_product_to_cart', label: 'Added product to cart' },
    { value: 'clicked_message', label: 'Clicked message' },
    { value: 'entered_segment', label: 'Entered segment' },
    { value: 'exited_segment', label: 'Exited segment' },
    { value: 'marked_spam', label: 'Marked message as spam' },
    { value: 'delivery_failed', label: 'Message delivery failed' },
    { value: 'message_sent', label: 'Message sent' },
    { value: 'opened_message', label: 'Opened message' },
    { value: 'order_canceled', label: 'Order canceled' },
    { value: 'order_fulfilled', label: 'Order fulfilled' },
    { value: 'order_refunded', label: 'Order refunded' },
    { value: 'ordered_product', label: 'Ordered product' },
    { value: 'paid_for_order', label: 'Paid for order' },
    { value: 'placed_order', label: 'Placed order' },
    { value: 'started_checkout', label: 'Started checkout' },
    { value: 'subscribed_marketing', label: 'Subscribed to Marketing' },
    { value: 'viewed_page', label: 'Viewed page' },
    { value: 'viewed_product', label: 'Viewed product' }
  ];

  const getLabelFrom = (list: { value: string; label: string }[], value: string) =>
    list.find(o => o.value === value)?.label || value;

  const humanizeContactFilter = () => {
    if (!contactFilterType) return '';
    switch (contactFilterType) {
      case 'date_added':
        return `Only contacts added in the last ${contactFilterNumber} days`;
      case 'gender':
        return contactFilterGender ? `Only contacts with gender ${contactFilterGender}` : '';
      case 'segment':
        return contactFilterValue ? `Only contacts in segment ${contactFilterValue}` : '';
      case 'tag':
        return contactFilterValue ? `Only contacts with tag ${contactFilterValue}` : '';
      case 'first_name':
        return contactFilterValue ? `Only contacts where first name is ${contactFilterValue}` : '';
      case 'last_name':
        return contactFilterValue ? `Only contacts where last name is ${contactFilterValue}` : '';
      case 'country':
      case 'state':
      case 'city':
      case 'postal_code':
        return contactFilterValue ? `Only contacts where ${contactFilterType.replace('_', ' ')} is ${contactFilterValue}` : '';
      default:
        return '';
    }
  };

  const buildSplitSummary = (filters: TriggerFilter[]) => {
    if (!filters || filters.length === 0) return '';
    return filters.reduce((summary, filter, index) => {
      const clause = describeFilter(filter);
      if (!clause) return summary;
      if (!summary) return clause;
      const previousLogic = filters[index - 1]?.logicOperator || 'AND';
      const logicWord = previousLogic === 'OR' ? 'or' : 'and';
      return `${summary} ${logicWord} ${clause}`;
    }, '');
  };

  const formatTriggerTitleFromEvent = (eventValue: string) => {
    if (!eventValue) return 'Define how a person enters a workflow';
    const phrase = TRIGGER_TITLE_PHRASES[eventValue];
    if (phrase) return `Customers enter the workflow when ${phrase}`;
    // Fallback: use label in a generic sentence
    const detail = TRIGGER_EVENT_DETAILS[eventValue];
    const label = (detail?.label || getLabelFrom(TRIGGER_EVENTS, eventValue)).toLowerCase();
    return `Customers enter the workflow when ${label}`;
  };

  const handleTriggerSelection = (option: TriggerCategoryOption) => {
    const detail = TRIGGER_EVENT_DETAILS[option.value];
    if (detail?.categoryId) {
      setActiveTriggerCategoryId(detail.categoryId);
    }
    setTriggerEvent(option.value);
    setTriggerFilters([]);
    setExitConditions([]);
    setContactFilterType('');
    setContactFilterValue('');
    setContactFilterNumber(30);
    setContactFilterGender('');
    setShowTriggerSelectionModal(false);
    const triggerStep = workflowElements.find(el => el.type === 'trigger');
    if (triggerStep) {
      setSelectedElement(triggerStep);
    }
  };

  // Live-update trigger node title/summary as selections change
  useEffect(() => {
    if (selectedElement?.type !== 'trigger') return;
    const contactFilter = contactFilterType
      ? (contactFilterType === 'date_added'
        ? { type: contactFilterType, lastNDays: contactFilterNumber }
        : contactFilterType === 'gender'
          ? { type: contactFilterType, gender: contactFilterGender }
          : { type: contactFilterType, value: contactFilterValue })
      : null;

    const eventDetail = triggerEvent ? TRIGGER_EVENT_DETAILS[triggerEvent] : undefined;
    const exitSummary = exitConditions.length > 0
      ? `Customers exit the workflow when ${exitConditions
        .map(c => getLabelFrom(EXIT_EVENTS, c.event))
        .filter(Boolean)
        .join(' or ')}`
      : '';
    const cfSummary = humanizeContactFilter();
    const baseDescription = eventDetail?.description || '';
    const description = [baseDescription, exitSummary, cfSummary].filter(Boolean).join('\n');
    const title = formatTriggerTitleFromEvent(triggerEvent);

    setWorkflowElements(prev => prev.map(el => {
      if (el.id !== selectedElement.id) return el;
      const previousConfig = el.config || {};
      const cfg: any = {
        ...previousConfig,
        skipAlreadyIn: triggerSkipAlreadyIn,
        windowText: triggerWindowText,
        skipOtherAutomations: triggerSkipOtherAutomations,
        exitConditions: exitConditions.map(ec => ({ id: ec.id, event: ec.event })),
        triggerFilters,
      };
      if (triggerEvent) {
        cfg.type = triggerEvent;
        cfg.event = triggerEvent;
        cfg.displayName = eventDetail?.label || previousConfig.displayName;
        cfg.description = eventDetail?.description || previousConfig.description;
        cfg.supportsFilters = Boolean(eventDetail?.supportsFilters);
      } else {
        delete cfg.type;
        delete cfg.event;
      }
      if (contactFilter) {
        cfg.contactFilter = contactFilter;
      } else {
        delete cfg.contactFilter;
      }
      return { ...el, title, description, config: cfg };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerEvent, triggerSkipAlreadyIn, triggerWindowText, triggerSkipOtherAutomations, contactFilterType, contactFilterValue, contactFilterNumber, contactFilterGender, exitConditions, triggerFilters]);

  // Live update AB Test title when sliders change
  useEffect(() => {
    if (selectedElement?.type !== 'ab_test') return;
    setWorkflowElements(prev => prev.map(el => (
      el.id === selectedElement.id ? { ...el, title: `A/B Test (${abTrafficA}% / ${abTrafficB}%)` } : el
    )));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abTrafficA, abTrafficB]);

  const toolboxItems: DragItem[] = [
    // Messages
    {
      type: 'email',
      title: 'Email',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    // SMS and Push notifications are hidden for now (not fully functional)
    // {
    //   type: 'sms',
    //   title: 'SMS',
    //   icon: (
    //     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    //     </svg>
    //   )
    // },
    // {
    //   type: 'push',
    //   title: 'Push notification',
    //   icon: (
    //     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.828 7l2.586 2.586a2 2 0 002.828 0L12.828 7H4.828zM4.828 17h8l-2.586-2.586a2 2 0 00-2.828 0L4.828 17z" />
    //     </svg>
    //   )
    // },
    // Flow Actions
    {
      type: 'delay',
      title: 'Delay',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12,6 12,12 16,14" />
        </svg>
      )
    },
    {
      type: 'tag',
      title: 'Tag contact',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      )
    },
    {
      type: 'split',
      title: 'Split',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    },
    {
      type: 'ab_test',
      title: 'A/B testing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    }
  ];

  const handleDragStart = (e: React.DragEvent, item: DragItem) => {
    setDraggedItem(item);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const backToAutomationsPath = isAdminTemplateWorkflow
    ? returnPath
    : effectiveWorkspaceId
      ? `/workspace/${effectiveWorkspaceId}/automations`
      : '/automations';
  const activeTriggerCategory =
    TRIGGER_CATEGORIES.find((category) => category.id === activeTriggerCategoryId) ?? TRIGGER_CATEGORIES[0];

  // Helper function to update exit element position based on the lowest action element
  const updateExitPosition = (elements: WorkflowElement[]): WorkflowElement[] => {
    const exitElement = elements.find(el => el.type === 'exit');
    if (!exitElement) return elements;

    // Find the lowest action element (excluding trigger and exit)
    const actionElements = elements
      .filter(el => el.type !== 'trigger' && el.type !== 'exit');

    if (actionElements.length === 0) {
      // No actions, keep exit at default position
      return elements;
    }

    const lowestElement = actionElements
      .sort((a, b) => b.y - a.y)[0]; // Get the element with highest y value (lowest on screen)

    // Position exit below the lowest element with spacing
    const newExitY = lowestElement.y + 120;

    return elements.map(el =>
      el.id === exitElement.id
        ? { ...el, y: newExitY }
        : el
    );
  };

  // Helper function to auto-layout all nodes in a vertical column
  const autoLayoutNodes = (elements: WorkflowElement[]): WorkflowElement[] => {
    const VERTICAL_SPACING = 150;
    const X_POSITION = 100;
    let currentY = 50;

    // Separate elements by type to ensure correct ordering
    const triggerElement = elements.find(el => el.type === 'trigger');
    const exitElement = elements.find(el => el.type === 'exit');
    const actionElements = elements
      .filter(el => el.type !== 'trigger' && el.type !== 'exit')
      .sort((a, b) => a.y - b.y); // Sort actions by their current Y position

    // Build the correct order: trigger, actions, exit
    const orderedElements = [
      ...(triggerElement ? [triggerElement] : []),
      ...actionElements,
      ...(exitElement ? [exitElement] : [])
    ];

    return orderedElements.map((el) => {
      const newElement = { ...el, x: X_POSITION, y: currentY };
      currentY += VERTICAL_SPACING;
      return newElement;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!draggedItem || !canvasRef.current) return;

    // Show a plus hint near the trigger column when in proximity
    const rect = canvasRef.current.getBoundingClientRect();
    const pointerX = e.clientX - rect.left;
    const pointerY = e.clientY - rect.top;
    const triggerElement = workflowElements.find(el => el.type === 'trigger');
    if (!triggerElement) { setDropHint(null); return; }

    const triggerCenterX = triggerElement.x + 96;
    const proximityX = Math.abs(pointerX - triggerCenterX) < 120; // near the trigger vertical line
    const proximityY = pointerY > triggerElement.y; // below trigger

    if (proximityX && proximityY) {
      // Predict the Y location using the same alignment logic as drop
      const lastElement = workflowElements
        .filter(el => el.type !== 'trigger' && el.type !== 'exit')
        .sort((a, b) => a.y - b.y)
        .pop();
      const hintY = lastElement ? lastElement.y + 120 : triggerElement.y + 120;
      setDropHint({ x: triggerCenterX, y: hintY });
    } else {
      setDropHint(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Find the trigger element to align new elements
    const triggerElement = workflowElements.find(el => el.type === 'trigger');

    // Calculate position for better alignment
    let newX = x - 100; // Center the element
    let newY = y - 25;

    // If we have a trigger, try to align elements in a straight line
    if (triggerElement) {
      newX = triggerElement.x; // Align horizontally with trigger

      // Find the last element in the workflow chain
      const lastElement = workflowElements
        .filter(el => el.type !== 'trigger' && el.type !== 'exit')
        .sort((a, b) => a.y - b.y)
        .pop();

      if (lastElement) {
        newY = lastElement.y + 120; // Place below the last element with spacing
      } else {
        newY = triggerElement.y + 120; // Place below trigger
      }
    }

    const newElement: WorkflowElement = {
      id: `${draggedItem.type}-${Date.now()}`,
      type: draggedItem.type as any,
      title: draggedItem.title,
      x: newX,
      y: newY,
      connections: [],
      config: {}
    };

    setWorkflowElements(prev => {
      const updated = [...prev, newElement];
      return updateExitPosition(updated);
    });
    setDraggedItem(null);
    setIsDragging(false);
    setDropHint(null);
  };

  const handleElementClick = (element: WorkflowElement) => {
    setSelectedElement(element);
  };

  const handleContextMenu = (elementId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ elementId, x: e.clientX, y: e.clientY });
  };

  const handleDeleteElement = (elementId: string) => {
    setWorkflowElements(prev => {
      const filtered = prev.filter(el => el.id !== elementId);
      return updateExitPosition(filtered);
    });
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
    setContextMenu(null);
  };

  const handleDuplicateElement = (elementId: string) => {
    const elementToDuplicate = workflowElements.find(el => el.id === elementId);
    if (elementToDuplicate) {
      const newElement: WorkflowElement = {
        ...elementToDuplicate,
        id: `${elementToDuplicate.type}-${Date.now()}`,
        y: elementToDuplicate.y + 120 // Place below the original
      };
      setWorkflowElements(prev => {
        const updated = [...prev, newElement];
        return updateExitPosition(updated);
      });
    }
    setContextMenu(null);
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Sync right-panel state when selection changes
  useEffect(() => {
    if (selectedElement?.type === 'delay') {
      const cfg = selectedElement.config || {};
      setDelayAmount(Number(cfg.amount ?? 1));
      setDelayUnit((cfg.unit ?? 'minutes') as 'minutes' | 'hours' | 'days' | 'weeks');
      setDelaySelectedDaysOnly(Boolean(cfg.onlySelectedDays ?? false));
      setShowEmailEditorModal(false);
    } else if (selectedElement?.type === 'email') {
      const cfg = selectedElement.config || {};
      setEmailName(String(cfg.emailName ?? cfg.name ?? ''));
      setEmailSubject(String(cfg.subject ?? ''));
      setEmailFromName(String(cfg.fromName ?? ''));
      setEmailFromEmail(String(cfg.fromEmail ?? ''));
      setEmailReplyTo(String(cfg.replyTo ?? ''));
      setEmailPreheader(String(cfg.preheader ?? ''));
      const trackingCfg = cfg.tracking || {};
      setEmailTrackOpens(trackingCfg.open !== undefined ? Boolean(trackingCfg.open) : true);
      setEmailTrackClicks(trackingCfg.click !== undefined ? Boolean(trackingCfg.click) : true);
      setEmailUtmTracking(
        cfg.utm !== undefined
          ? Boolean(cfg.utm)
          : trackingCfg.utm !== undefined
            ? Boolean(trackingCfg.utm)
            : false
      );
      setEmailTemplateId(String(cfg.emailTemplateId ?? ''));
      setEmailTemplateName(String(cfg.emailTemplateName ?? ''));
      const sendOptions = (cfg.sendOptions || {}) as {
        mode?: 'immediate' | 'delay' | 'specific_time';
        delayValue?: number;
        delayUnit?: 'minutes' | 'hours' | 'days';
        sendTime?: string;
        timezone?: string;
      };
      setEmailSendMode((sendOptions.mode as 'immediate' | 'delay' | 'specific_time') || 'immediate');
      setEmailDelayAmount(Number(sendOptions.delayValue ?? 0));
      setEmailDelayUnit((sendOptions.delayUnit as 'minutes' | 'hours' | 'days') || 'hours');
      setEmailSendTime(String(sendOptions.sendTime ?? '09:00'));
      const timezone = typeof sendOptions.timezone === 'string' ? sendOptions.timezone : undefined;
      if (timezone) {
        setEmailSendTimezone(timezone);
      }
      const conditional = cfg.conditionalSend || {};
      setEmailConditionalSendEnabled(Boolean(conditional.enabled));
      setEmailConditionalTag(String(conditional.tagId ?? conditional.tagName ?? ''));
      const abCfg = cfg.abTest || {};
      setEmailABTestEnabled(Boolean(abCfg.enabled));
      setEmailABSubjectB(String(abCfg.subjectB ?? cfg.subjectB ?? ''));
      setEmailABSplit(Number(abCfg.split ?? 50));
      setEmailTemplateMissing(false);
      setEmailAnalyticsPreview(cfg.analyticsPreview ?? null);
      setShowEmailEditorModal(true);
    } else if (selectedElement?.type === 'split') {
      const cfg = selectedElement.config || {};
      const filters = Array.isArray(cfg.filters) ? cfg.filters : [];
      const yesId = cfg.yesStepId ?? (Array.isArray(selectedElement.connections) ? selectedElement.connections[0] : null);
      const noId = cfg.noStepId ?? (Array.isArray(selectedElement.connections) ? selectedElement.connections[1] : null);
      setSplitFilters(filters);
      setSplitYesPath(yesId ? String(yesId) : '__end__');
      setSplitNoPath(noId ? String(noId) : '__end__');
      setShowEmailEditorModal(false);
    } else if (selectedElement?.type === 'trigger' || !selectedElement) {
      // if nothing selected, default to trigger editing baseline values
      const triggerEl = (selectedElement?.type === 'trigger'
        ? selectedElement
        : workflowElements.find(el => el.type === 'trigger')) as WorkflowElement | undefined;
      const cfg = triggerEl?.config || {};
      setTriggerEvent(String(cfg.event ?? ''));
      setTriggerSkipAlreadyIn(Boolean(cfg.skipAlreadyIn ?? true));
      setTriggerWindowText(String(cfg.windowText ?? 'in the last 1 day'));
      setTriggerSkipOtherAutomations(Boolean(cfg.skipOtherAutomations ?? false));
      const cf = cfg.contactFilter || {};
      setContactFilterType(String(cf.type ?? ''));
      setContactFilterValue(String(cf.value ?? ''));
      setContactFilterNumber(Number(cf.lastNDays ?? 30));
      setContactFilterGender(String(cf.gender ?? ''));
      const ec = Array.isArray(cfg.exitConditions) ? cfg.exitConditions : [];
      setExitConditions(ec.map((e: any, idx: number) => ({ id: e.id || `ec-${idx}`, event: String(e.event || '') })));
      setShowEmailEditorModal(false);
    }
  }, [selectedElement]);

  useEffect(() => {
    let isMounted = true;
    const loadDomains = async () => {
      const defaultOptions: Array<{ label: string; value: string; status: 'default' | 'verified' | 'active' | 'pending' | 'failed' | 'warming'; domainId?: string }> = [
        { label: 'Use workspace default (info@scubamail.io)', value: 'info@scubamail.io', status: 'default' },
        { label: 'Use transactional no-reply (noreply@scubamail.com)', value: 'noreply@scubamail.com', status: 'default' },
      ];

      if (selectedElement?.type !== 'email') {
        if (isMounted) {
          setEmailSendingDomains([]);
          setEmailFromEmailOptions(defaultOptions);
          evaluateFromEmailStatus(emailFromEmail || '', []);
        }
        return;
      }

      if (!currentUser?.uid) {
        if (isMounted) {
          setEmailSendingDomains([]);
          setEmailFromEmailOptions(defaultOptions);
          evaluateFromEmailStatus(emailFromEmail || '', []);
        }
        return;
      }

      try {
        const domains = await sendingDomainService.getUserSendingDomains(ownerId!);
        if (!isMounted) return;
        setEmailSendingDomains(domains);
        const domainOptions = domains.map(domain => ({
          label:
            domain.status === 'verified' || domain.status === 'active'
              ? `${domain.domain} (${domain.status})`
              : `${domain.domain} (${domain.status})`,
          value: `hello@${domain.domain}`,
          status: domain.status,
          domainId: domain.id,
        }));
        const combined = [...defaultOptions, ...domainOptions];
        setEmailFromEmailOptions(combined);
        evaluateFromEmailStatus(emailFromEmail || '', domains);
      } catch (error) {
        console.error('Failed to load sending domains:', error);
        if (isMounted) {
          setEmailSendingDomains([]);
          setEmailFromEmailOptions(defaultOptions);
          evaluateFromEmailStatus(emailFromEmail || '', []);
        }
      }
    };

    loadDomains();
    return () => {
      isMounted = false;
    };
  }, [selectedElement?.id, selectedElement?.type, currentUser?.uid, ownerId, emailFromEmail, evaluateFromEmailStatus]);

  useEffect(() => {
    let isMounted = true;
    const ensureTemplate = async () => {
      if (!emailTemplateId) {
        if (isMounted) {
          setEmailTemplateMissing(false);
        }
        return;
      }
      try {
        const template = await getEmailTemplate(emailTemplateId);
        if (!isMounted) return;
        if (!template) {
          setEmailTemplateMissing(true);
        } else {
          setEmailTemplateMissing(false);
          if (!emailTemplateName) {
            setEmailTemplateName(template.name || '');
          }
        }
      } catch (error) {
        console.error('Failed to load email template:', error);
        if (isMounted) {
          setEmailTemplateMissing(true);
        }
      }
    };
    ensureTemplate();
    return () => {
      isMounted = false;
    };
  }, [emailTemplateId, emailTemplateName]);

  // Hydrate draft from localStorage only when there is no server-backed automation id yet.
  // If automationId is set, Firestore is the source of truth; a mount-only restore was overwriting
  // empty new automations with stale cache from the same or a fallback storage key.
  useEffect(() => {
    if (automationId) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.elements)) {
          setWorkflowElements(updateExitPosition(parsed.elements));
        }
      }
    } catch (_) { }
  }, [storageKey, automationId]);

  const saveWorkflowToStorage = () => {
    try {
      const payload = { elements: workflowElements };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (_) { }
  };

  // Save automation to database. Returns the saved document id on success, or false on failure.
  const handleSaveAutomation = async (): Promise<string | false> => {
    if (!currentUser) {
      showError('You must be logged in to save automations');
      return false;
    }

    const errors = validateWorkflow();
    setValidationErrors(errors);
    const isActiveAutomation = Boolean(automationId && automationStatus === 'active');
    if (errors.length > 0 && isActiveAutomation) {
      showError(errors[0] || 'Please fix workflow errors before saving an active automation');
      return false;
    }

    setLoading(true);
    try {
      const existingDocId =
        typeof automationId === 'string' && automationId.length > 0 ? automationId : undefined;

      const canvasInputs = workflowElements.map((el) => {
        let config = el.config;

        // If trigger is birthday, convert to anniversary format (storage / triggers)
        if (el.type === 'trigger' && config?.event === 'birthday') {
          config = {
            ...config,
            type: 'anniversary',
            event: 'anniversary',
            config: {
              ...config.config,
              anniversaryField: 'birthday',
              daysBefore: config.config?.daysBefore || 0,
              daysAfter: config.config?.daysAfter || 0
            }
          };
        }

        return {
          id: el.id,
          type: el.type,
          title: el.title,
          description: el.description || '',
          config,
          position: { x: el.x, y: el.y },
          connections: el.connections,
          enabled: true
        };
      });

      const processedSteps = canvasStepsToAutomationSteps(canvasInputs);

      const automationData = {
        name: automationName,
        description: 'Automated workflow',
        status: existingDocId ? automationStatus : ('draft' as const),
        userId: currentUser.uid,
        ...(effectiveWorkspaceId ? { workspaceId: effectiveWorkspaceId } : {}),
        steps: processedSteps,
        settings: {
          allowRepetition: false,
          openTracking: true,
          clickTracking: true,
          personalizedToField: true,
          googleAnalyticsTracking: false
        }
      };

      let savedAutomationId: string;
      if (existingDocId) {
        await automationService.updateAutomation(existingDocId, automationData);
        savedAutomationId = existingDocId;
      } else {
        const newId = await automationService.createAutomation(automationData);
        // Admin template builder: stay in admin flow; caller navigates back to template editor with id.
        if (!isAdminTemplateWorkflow) {
          if (effectiveWorkspaceId) {
            navigate(`/workspace/${effectiveWorkspaceId}/automations/edit/${newId}`, { replace: true });
          } else {
            navigate(`/automations/edit/${newId}`, { replace: true });
          }
        }
        savedAutomationId = newId;
      }

      setValidationErrors([]);
      saveWorkflowToStorage();
      return savedAutomationId;
    } catch (error) {
      console.error('Error saving automation:', error);
      showError('Failed to save automation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Save as template function
  const handleSaveAsTemplate = async (metadata: Partial<AutomationTemplate>) => {
    if (!currentUser) {
      showError('You must be logged in to save templates');
      return;
    }

    setLoading(true);
    try {
      const canvasInputs = workflowElements.map((el) => {
        let config = el.config;

        // If trigger is birthday, convert to anniversary format
        if (el.type === 'trigger' && config?.event === 'birthday') {
          config = {
            ...config,
            type: 'anniversary',
            event: 'anniversary',
            config: {
              ...config.config,
              anniversaryField: 'birthday',
              daysBefore: config.config?.daysBefore || 0,
              daysAfter: config.config?.daysAfter || 0
            }
          };
        }

        return {
          id: el.id,
          type: el.type,
          title: el.title,
          description: el.description || '',
          config,
          position: { x: el.x, y: el.y },
          connections: el.connections,
          enabled: true
        };
      });

      const processedTemplateSteps = canvasStepsToAutomationSteps(canvasInputs);

      const templateData: Omit<AutomationTemplate, 'id'> = {
        name: metadata.name || automationName,
        description: metadata.description || '',
        category: metadata.category || 'ecommerce',
        type: metadata.type || '',
        goal: metadata.goal || '',
        channel: metadata.channel || 'Email',
        estimatedDuration: metadata.estimatedDuration || '1 day',
        tags: metadata.tags || [],
        badge: metadata.badge || '',
        isRecommended: metadata.isRecommended || false,
        isActive: metadata.isActive !== undefined ? metadata.isActive : true,
        isPublic: metadata.isPublic !== undefined ? metadata.isPublic : true,
        isPremium: metadata.isPremium || false,
        price: metadata.price || 0,
        difficulty: metadata.difficulty || 'beginner',
        steps: processedTemplateSteps,
        createdBy: currentUser.uid,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
        usageCount: 0,
        downloadCount: 0
      };

      if (templateId) {
        // Update existing template
        await automationTemplateService.updateTemplate(templateId, templateData);
      } else {
        // Create new template
        await automationTemplateService.createTemplate(templateData);
      }

      // Navigate back to admin panel
      navigate(returnPath);
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  // ── Test Run ─────────────────────────────────────────────────────────────
  const [testRunLoading, setTestRunLoading] = useState(false);
  const [showTestRunModal, setShowTestRunModal] = useState(false);
  const [testContactEmail, setTestContactEmail] = useState('');

  const handleTestRun = async () => {
    const email = testContactEmail.trim() || currentUser?.email || '';
    if (!automationId) {
      showError('Save the automation first before running a test.');
      return;
    }
    if (!email) {
      showError('Enter a contact email to test with.');
      return;
    }
    setTestRunLoading(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions: fns } = await import('../firebase');
      const testFn = httpsCallable(fns, 'testAutomation');
      const result: any = await testFn({
        automationId,
        contactEmail: email,
        workspaceId: effectiveWorkspaceId,
      });
      if (result.data?.success) {
        setEditorView('activity');
      } else {
        showError(result.data?.error || 'Test run failed to start.');
      }
    } catch (err: any) {
      showError(err?.message || 'Test run failed.');
    } finally {
      setTestRunLoading(false);
      setShowTestRunModal(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleSaveAndClose = async () => {
    if (isTemplateMode && !isAdminTemplateWorkflow) {
      // Show template metadata modal
      setShowTemplateMetadataModal(true);
    } else {
      // Regular automation save (or admin template workflow step save)
      const savedAutomationId = await handleSaveAutomation();
      if (savedAutomationId) {
        if (isAdminTemplateWorkflow) {
          sessionStorage.setItem('admin_template_builder_automation_id', savedAutomationId);
          navigate(withAutomationIdInReturnPath(returnPath, savedAutomationId));
        } else {
          navigate(effectiveWorkspaceId ? `/workspace/${effectiveWorkspaceId}/automations` : '/automations');
        }
      }
    }
  };

  const validateWorkflow = (): string[] => {
    const errors: string[] = [];
    const trigger = workflowElements.find(el => el.type === 'trigger');
    const actions = workflowElements.filter(el => el.type !== 'trigger' && el.type !== 'exit');
    if (!trigger || !trigger.config || !trigger.config.event) {
      errors.push('Trigger event is required.');
    }
    if (actions.length === 0) {
      errors.push('Add at least one action after the trigger.');
    }
    // Example email validation
    const emailActions = actions.filter(el => el.type === 'email');
    const emailsMissingSubject = emailActions.filter(el => !(el.config && el.config.subject));
    if (emailsMissingSubject.length > 0) {
      errors.push('Email actions must have a subject.');
    }
    const emailsMissingTemplate = emailActions.filter(el => !(el.config && el.config.emailTemplateId));
    if (emailsMissingTemplate.length > 0) {
      errors.push('Email actions must have an email template selected.');
    }
    const emailsMissingFromEmail = emailActions.filter(el => !(el.config && el.config.fromEmail));
    if (emailsMissingFromEmail.length > 0) {
      errors.push('Email actions must specify a From email address.');
    }
    const stepIds = new Set(workflowElements.map(el => el.id));

    // Block unsupported channel types that have no provider wired
    const unsupportedSteps = actions.filter(el => el.type === 'sms' || el.type === 'push');
    if (unsupportedSteps.length > 0) {
      errors.push('SMS and Push notification steps are not yet available. Remove them before activating.');
    }

    const splitSteps = actions.filter(el => el.type === 'split');
    splitSteps.forEach(split => {
      const cfg = split.config || {};
      const filters = Array.isArray(cfg.filters) ? cfg.filters : [];
      const yesId = cfg.yesStepId ?? (Array.isArray(split.connections) ? split.connections[0] : null);
      const noId = cfg.noStepId ?? (Array.isArray(split.connections) ? split.connections[1] : null);
      if (filters.length === 0) {
        errors.push(`Split "${split.title || 'Split'}" must have at least one condition.`);
      }
      if (!yesId && !noId) {
        errors.push(`Split "${split.title || 'Split'}" must route contacts to a YES or NO branch.`);
      }
      if (yesId && !stepIds.has(yesId)) {
        errors.push(`Split "${split.title || 'Split'}" YES branch points to a missing step.`);
      }
      if (noId && !stepIds.has(noId)) {
        errors.push(`Split "${split.title || 'Split'}" NO branch points to a missing step.`);
      }
    });
    return errors;
  };

  const handleStartStop = async () => {
    const isCurrentlyActive = automationStatus === 'active';
    
    if (!isCurrentlyActive) {
      // Starting the workflow
      const errors = validateWorkflow();
      setValidationErrors(errors);
      if (errors.length === 0) {
        try {
          setLoading(true);
          
          // Save the automation with active status
          const automationData = {
            name: automationName,
            status: 'active' as const,
            steps: canvasStepsToAutomationSteps(workflowElements),
            userId: currentUser?.uid || '',
            workspaceId: effectiveWorkspaceId || '',
            settings: {
              allowRepetition: false,
              openTracking: true,
              clickTracking: true,
              personalizedToField: true,
              googleAnalyticsTracking: false,
            },
          };

          if (automationId) {
            // Update existing automation
            await automationService.updateAutomation(automationId, {
              ...automationData,
            });
          } else {
            // Create new automation
            await automationService.createAutomation(automationData);
          }

          // Clear localStorage
          try {
            localStorage.removeItem(storageKey);
          } catch (e) {
            console.warn('Could not clear localStorage:', e);
          }

          setAutomationStatus('active');
          setIsRunning(true);
          
          // Navigate back to automations page after a short delay
          setTimeout(() => {
            navigate(returnPath);
          }, 1000);
        } catch (error) {
          console.error('Error starting automation:', error);
          showError('Failed to start automation');
          setLoading(false);
        }
      }
    } else {
      // Pausing the workflow
      if (automationId) {
        try {
          setLoading(true);
          await automationService.updateAutomation(automationId, {
            status: 'paused',
          });
          setIsRunning(false);
          setAutomationStatus('paused');
          setLoading(false);
        } catch (error) {
          console.error('Error pausing automation:', error);
          showError('Failed to pause automation');
          setLoading(false);
        }
      } else {
        setIsRunning(false);
        setAutomationStatus('draft');
      }
    }
  };

  const formatDelayTitle = (amount: number, unit: 'minutes' | 'hours' | 'days' | 'weeks') => {
    const singularMap: Record<typeof unit, string> = {
      minutes: 'minute',
      hours: 'hour',
      days: 'day',
      weeks: 'week'
    } as const;
    const label = amount === 1 ? singularMap[unit] : unit;
    return `Wait for ${amount} ${label}`;
  };
  const selectedElementId = selectedElement?.id;
  const applyPersonalization = useCallback((templateContent: string, contactData: Record<string, any>) => {
    if (!templateContent) return '';
    return templateContent.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
      const normalizedKey = key.toLowerCase();
      const replacements = [
        contactData?.[key],
        contactData?.[normalizedKey],
        contactData?.[key.replace(/_/g, '')],
      ].filter(value => value !== undefined && value !== null);
      if (replacements.length > 0) {
        return String(replacements[0]);
      }
      return '';
    });
  }, []);
  const handleInsertSubjectToken = useCallback((token: string) => {
    if (!token) return;
    setEmailSubject(prev => {
      const base = prev || '';
      const needsSeparator = base.length > 0 && !base.endsWith(' ');
      return `${base}${needsSeparator ? ' ' : ''}${token}`;
    });
  }, []);
  const handleTemplateSelected = useCallback(async (template: EmailTemplate) => {
    setEmailTemplateId(template.id || '');
    setEmailTemplateName(template.name || '');
    setEmailTemplateMissing(false);
    setEmailTemplateModalOpen(false);

    // Automatically generate preview when template is selected
    if (template.id) {
      setEmailPreviewLoading(true);
      try {
        const fullTemplate = await getEmailTemplate(template.id);
        if (fullTemplate) {
          const sampleContact = {
            first_name: 'Amelia',
            last_name: 'Stone',
            name: 'Amelia Stone',
            email: currentUser?.email || 'subscriber@example.com',
            company: automationName || 'Your brand',
            city: 'San Francisco',
            country: 'USA',
          };
          const processedHtml = applyPersonalization(fullTemplate.htmlContent, sampleContact);
          setEmailPreviewHtml(processedHtml);
        }
      } catch (error) {
        console.error('Failed to generate preview:', error);
      } finally {
        setEmailPreviewLoading(false);
      }
    }
  }, [currentUser?.email, automationName, applyPersonalization]);
  const handlePreviewEmail = useCallback(async () => {
    if (!emailTemplateId) {
      showError('Select an email template to preview.');
      return;
    }
    setEmailPreviewLoading(true);
    try {
      const template = await getEmailTemplate(emailTemplateId);
      if (!template) {
        setEmailTemplateMissing(true);
        showError('Selected template could not be found.');
        return;
      }
      const sampleContact = {
        first_name: 'Amelia',
        last_name: 'Stone',
        name: 'Amelia Stone',
        email: currentUser?.email || 'subscriber@example.com',
        company: automationName || 'Your brand',
        city: 'San Francisco',
        country: 'USA',
      };
      const processedHtml = applyPersonalization(template.htmlContent, sampleContact);
      setEmailPreviewHtml(processedHtml);
      setEmailPreviewModalOpen(true);
      setEmailTemplateMissing(false);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      showError('Unable to generate a preview for this template right now.');
    } finally {
      setEmailPreviewLoading(false);
    }
  }, [emailTemplateId, currentUser?.email, automationName, applyPersonalization, showError]);
  const handleSendTestEmail = useCallback(async () => {
    if (!currentUser?.email) {
      showError('Sign in with a verified email address to send a test.');
      return;
    }
    if (!emailTemplateId) {
      showError('Select an email template before sending a test email.');
      return;
    }
    setEmailTestSending(true);
    try {
      const template = await getEmailTemplate(emailTemplateId);
      if (!template) {
        setEmailTemplateMissing(true);
        showError('Selected template could not be found.');
        return;
      }
      const [firstName, ...rest] = (currentUser.displayName || '').split(' ').filter(Boolean);
      const testContact = {
        first_name: firstName || 'Test',
        last_name: rest.join(' ') || 'Recipient',
        name: currentUser.displayName || 'Test Recipient',
        email: currentUser.email,
        company: automationName || 'Scubamail',
      };
      const processedHtml = applyPersonalization(template.htmlContent, testContact);
      const processedText = applyPersonalization(template.textContent || '', testContact);
      const trimmedSubject = emailSubject.trim();
      const subject = applyPersonalization(trimmedSubject || template.subject || 'Automation Test Email', testContact);
      const fromEmailValue = emailFromEmail || 'info@scubamail.io';
      const fromNameValue = emailFromName || automationName || 'Scubamail';
      const sendResult = await secureEmailService.sendEmail({
        from: { email: fromEmailValue, name: fromNameValue },
        to: [{ email: currentUser.email, name: testContact.name }],
        subject,
        htmlBody: processedHtml,
        textBody: processedText,
        replyTo: emailReplyTo || fromEmailValue,
        tags: {
          Source: 'AutomationTest',
          AutomationId: automationId || 'preview',
          ActionId: selectedElementId || 'preview',
        },
      });
      if (!sendResult.success) {
        showError(sendResult.error || 'Failed to send test email');
      }
    } catch (error: any) {
      console.error('Failed to send test email:', error);
      showError(error?.message || 'Failed to send test email');
    } finally {
      setEmailTestSending(false);
    }
  }, [
    currentUser,
    emailTemplateId,
    applyPersonalization,
    emailSubject,
    emailFromEmail,
    emailFromName,
    emailReplyTo,
    automationName,
    automationId,
    selectedElementId,
    showError,
  ]);
  const handleFromEmailChange = useCallback((value: string) => {
    setEmailFromEmail(value);
    evaluateFromEmailStatus(value, emailSendingDomains);
  }, [emailSendingDomains, evaluateFromEmailStatus]);

  const handleUpdateSelected = () => {
    if (!selectedElement) return;
    if (selectedElement.type === 'delay') {
      const updatedTitle = formatDelayTitle(delayAmount, delayUnit);
      setWorkflowElements(prev => prev.map(el => (
        el.id === selectedElement.id
          ? { ...el, title: updatedTitle, config: { amount: delayAmount, unit: delayUnit, onlySelectedDays: delaySelectedDaysOnly } }
          : el
      )));
    } else if (selectedElement.type === 'trigger') {
      const contactFilter = contactFilterType
        ? (contactFilterType === 'date_added'
          ? { type: contactFilterType, lastNDays: contactFilterNumber }
          : contactFilterType === 'gender'
            ? { type: contactFilterType, gender: contactFilterGender }
            : { type: contactFilterType, value: contactFilterValue })
        : null;
      setWorkflowElements(prev => prev.map(el => {
        if (el.id !== selectedElement.id) return el;
        // Build human summary
        const exitSummary = exitConditions.length > 0
          ? `Customers exit the workflow when ${exitConditions
            .map(c => getLabelFrom(EXIT_EVENTS, c.event))
            .filter(Boolean)
            .join(' or ')}`
          : '';
        const cfSummary = humanizeContactFilter();
        const description = [exitSummary, cfSummary].filter(Boolean).join('\n');
        const cfg: any = {
          event: triggerEvent,
          skipAlreadyIn: triggerSkipAlreadyIn,
          windowText: triggerWindowText,
          skipOtherAutomations: triggerSkipOtherAutomations,
          exitConditions: exitConditions.map(ec => ({ id: ec.id, event: ec.event })),
          triggerFilters: triggerFilters // Save trigger filters
        };
        if (contactFilter) cfg.contactFilter = contactFilter;
        return { ...el, config: cfg, description };
      }));
    } else if (selectedElement.type === 'split') {
      const yesId = splitYesPath && splitYesPath !== '__end__' ? splitYesPath : null;
      const noId = splitNoPath && splitNoPath !== '__end__' ? splitNoPath : null;
      const summary = buildSplitSummary(splitFilters);
      const yesTargetTitle = yesId
        ? workflowElements.find(el => el.id === yesId)?.title || 'Next step'
        : 'Stop automation';
      const noTargetTitle = noId
        ? workflowElements.find(el => el.id === noId)?.title || 'Next step'
        : 'Stop automation';
      const descriptionParts = [
        summary ? `Condition: ${summary}` : '',
        `Yes → ${yesTargetTitle}`,
        `No → ${noTargetTitle}`
      ].filter(Boolean);
      const newConfig = {
        filters: splitFilters,
        yesStepId: yesId,
        noStepId: noId
      };
      const newConnections = [yesId, noId].filter(Boolean) as string[];
      const newTitle = summary ? `Split: ${summary}` : 'Split';
      const newDescription = descriptionParts.join('\n');
      setWorkflowElements(prev => prev.map(el => (
        el.id === selectedElement.id
          ? { ...el, title: newTitle, description: newDescription, config: newConfig, connections: newConnections }
          : el
      )));
      setSelectedElement(prev => prev && prev.id === selectedElement.id
        ? { ...prev, title: newTitle, description: newDescription, config: newConfig, connections: newConnections }
        : prev
      );
    } else if (selectedElement.type === 'email') {
      const trimmedName = emailName.trim();
      const trimmedSubject = emailSubject.trim();
      const title = trimmedName
        ? `Send email: ${trimmedName}`
        : trimmedSubject
          ? `Send email: ${trimmedSubject}`
          : 'Send email';

      const sendOptions =
        emailSendMode === 'delay'
          ? {
            mode: 'delay' as const,
            delayValue: emailDelayAmount,
            delayUnit: emailDelayUnit,
          }
          : emailSendMode === 'specific_time'
            ? {
              mode: 'specific_time' as const,
              sendTime: emailSendTime,
              timezone: emailSendTimezone,
            }
            : { mode: 'immediate' as const };

      const trackingConfig = {
        open: emailTrackOpens,
        click: emailTrackClicks,
        utm: emailUtmTracking,
      };

      const conditionalSendConfig = emailConditionalSendEnabled && emailConditionalTag
        ? {
          enabled: true,
          tagId: emailConditionalTag,
        }
        : emailConditionalSendEnabled
          ? { enabled: true }
          : undefined;

      const abTestConfig = emailABTestEnabled && emailABSubjectB.trim()
        ? {
          enabled: true,
          subjectB: emailABSubjectB.trim(),
          split: Math.min(Math.max(emailABSplit, 1), 99),
        }
        : emailABTestEnabled
          ? {
            enabled: true,
            split: Math.min(Math.max(emailABSplit, 1), 99),
          }
          : undefined;

      const config = {
        emailName: trimmedName,
        emailTemplateId: emailTemplateId || undefined,
        emailTemplateName: emailTemplateName || undefined,
        subject: trimmedSubject,
        fromName: emailFromName || undefined,
        fromEmail: emailFromEmail || undefined,
        replyTo: emailReplyTo || undefined,
        preheader: emailPreheader || undefined,
        utm: emailUtmTracking,
        tracking: trackingConfig,
        sendOptions,
        conditionalSend: conditionalSendConfig,
        abTest: abTestConfig,
        analyticsPreview: emailAnalyticsPreview || undefined,
      };

      const summaryParts: string[] = [];
      if (emailTemplateName) summaryParts.push(`Template: ${emailTemplateName}`);
      if (emailSendMode === 'delay') {
        summaryParts.push(`Timing: send after ${emailDelayAmount} ${emailDelayUnit}`);
      } else if (emailSendMode === 'specific_time') {
        summaryParts.push(`Timing: send at ${emailSendTime} (${emailSendTimezone})`);
      } else {
        summaryParts.push('Timing: send immediately');
      }
      if (!emailTrackOpens || !emailTrackClicks) {
        const disabledTracking = [
          !emailTrackOpens ? 'opens' : null,
          !emailTrackClicks ? 'clicks' : null,
        ]
          .filter(Boolean)
          .join(' & ');
        summaryParts.push(`Tracking disabled for ${disabledTracking}`);
      }
      if (emailConditionalSendEnabled) {
        summaryParts.push(
          emailConditionalTag
            ? `Conditional: only send to contacts with tag "${emailConditionalTag}"`
            : 'Conditional: sending limited by tag rule'
        );
      }
      if (emailABTestEnabled) {
        summaryParts.push(
          `A/B test active — ${Math.min(Math.max(emailABSplit, 1), 99)}% variant A / ${100 - Math.min(Math.max(emailABSplit, 1), 99)}% variant B`
        );
      }

      const description = summaryParts.join('\n');

      setWorkflowElements(prev => prev.map(el => (
        el.id === selectedElement.id
          ? { ...el, title, description, config }
          : el
      )));
      setSelectedElement(prev => (prev && prev.id === selectedElement.id ? { ...prev, title, description, config } : prev));
    } else if (selectedElement.type === 'ab_test') {
      const title = `A/B Test (${abTrafficA}% / ${abTrafficB}%)`;
      setWorkflowElements(prev => prev.map(el => (
        el.id === selectedElement.id
          ? { ...el, title, config: { trafficA: abTrafficA, trafficB: abTrafficB, goal: abGoal, windowHours: abWindowHours, autoWinner: abAutoWinner } }
          : el
      )));
    }
  };

  const handleCancelEdit = () => {
    if (selectedElement?.type === 'delay') {
      const cfg = selectedElement.config || {};
      setDelayAmount(Number(cfg.amount ?? 1));
      setDelayUnit((cfg.unit ?? 'minutes') as 'minutes' | 'hours' | 'days' | 'weeks');
      setDelaySelectedDaysOnly(Boolean(cfg.onlySelectedDays ?? false));
    } else if (selectedElement?.type === 'email') {
      const cfg = selectedElement.config || {};
      const sendOptions = (cfg.sendOptions || {}) as {
        mode?: 'immediate' | 'delay' | 'specific_time';
        delayValue?: number;
        delayUnit?: 'minutes' | 'hours' | 'days';
        sendTime?: string;
        timezone?: string;
      };
      const trackingCfg = cfg.tracking || {};
      const conditionalCfg = cfg.conditionalSend || {};
      const abCfg = cfg.abTest || {};
      setEmailName(String(cfg.emailName ?? cfg.name ?? ''));
      setEmailSubject(String(cfg.subject ?? ''));
      setEmailFromName(String(cfg.fromName ?? ''));
      setEmailFromEmail(String(cfg.fromEmail ?? ''));
      setEmailReplyTo(String(cfg.replyTo ?? ''));
      setEmailPreheader(String(cfg.preheader ?? ''));
      setEmailUtmTracking(
        cfg.utm !== undefined
          ? Boolean(cfg.utm)
          : trackingCfg.utm !== undefined
            ? Boolean(trackingCfg.utm)
            : false
      );
      setEmailTemplateId(String(cfg.emailTemplateId ?? ''));
      setEmailTemplateName(String(cfg.emailTemplateName ?? ''));
      setEmailSendMode((sendOptions.mode as 'immediate' | 'delay' | 'specific_time') || 'immediate');
      setEmailDelayAmount(Number(sendOptions.delayValue ?? 0));
      setEmailDelayUnit((sendOptions.delayUnit as 'minutes' | 'hours' | 'days') || 'hours');
      setEmailSendTime(String(sendOptions.sendTime ?? '09:00'));
      const timezone = typeof sendOptions.timezone === 'string' ? sendOptions.timezone : undefined;
      if (timezone) {
        setEmailSendTimezone(timezone);
      }
      setEmailTrackOpens(trackingCfg.open !== undefined ? Boolean(trackingCfg.open) : true);
      setEmailTrackClicks(trackingCfg.click !== undefined ? Boolean(trackingCfg.click) : true);
      setEmailConditionalSendEnabled(Boolean(conditionalCfg?.enabled));
      setEmailConditionalTag(String(conditionalCfg?.tagId ?? conditionalCfg?.tagName ?? ''));
      setEmailABTestEnabled(Boolean(abCfg?.enabled));
      setEmailABSubjectB(String(abCfg?.subjectB ?? cfg.subjectB ?? ''));
      setEmailABSplit(Number(abCfg?.split ?? 50));
    } else if (selectedElement?.type === 'trigger') {
      const cfg = selectedElement.config || {};
      setTriggerEvent(String(cfg.event ?? ''));
      setTriggerSkipAlreadyIn(Boolean(cfg.skipAlreadyIn ?? true));
      setTriggerWindowText(String(cfg.windowText ?? 'in the last 1 day'));
      setTriggerSkipOtherAutomations(Boolean(cfg.skipOtherAutomations ?? false));
      const cf = cfg.contactFilter || {};
      setContactFilterType(String(cf.type ?? ''));
      setContactFilterValue(String(cf.value ?? ''));
      setContactFilterNumber(Number(cf.lastNDays ?? 30));
      setContactFilterGender(String(cf.gender ?? ''));
      const ec = Array.isArray(cfg.exitConditions) ? cfg.exitConditions : [];
      setExitConditions(ec.map((e: any, idx: number) => ({ id: e.id || `ec-${idx}`, event: String(e.event || '') })));
      // Load trigger filters
      const filters = Array.isArray(cfg.triggerFilters) ? cfg.triggerFilters : [];
      setTriggerFilters(filters);
    } else if (selectedElement?.type === 'split') {
      const cfg = selectedElement.config || {};
      const filters = Array.isArray(cfg.filters) ? cfg.filters : [];
      const yesId = cfg.yesStepId ?? (Array.isArray(selectedElement.connections) ? selectedElement.connections[0] : null);
      const noId = cfg.noStepId ?? (Array.isArray(selectedElement.connections) ? selectedElement.connections[1] : null);
      setSplitFilters(filters);
      setSplitYesPath(yesId ? String(yesId) : '__end__');
      setSplitNoPath(noId ? String(noId) : '__end__');
    } else if (selectedElement?.type === 'ab_test') {
      const cfg = selectedElement.config || {};
      setAbTrafficA(Number(cfg.trafficA ?? 50));
      setAbTrafficB(Number(cfg.trafficB ?? 50));
      setAbGoal(String(cfg.goal ?? 'open_rate'));
      setAbWindowHours(Number(cfg.windowHours ?? 24));
      setAbAutoWinner(Boolean(cfg.autoWinner ?? true));
    }
  };

  // Get available filter fields based on trigger type
  const getAvailableFieldsForTrigger = (triggerType: string): FilterFieldDefinition[] => {
    const ecommerceTriggers = [
      'added_product_to_cart',
      'started_checkout',
      'abandoned_cart',
      'placed_order',
      'viewed_product'
    ];

    if (ecommerceTriggers.includes(triggerType)) {
      return ADDED_TO_CART_FIELDS;
    }

    // For other triggers, return an empty array (no filters available yet)
    // You can add more field definitions for other trigger types in the future
    return [];
  };

  const getElementIcon = (type: string) => {
    switch (type) {
      case 'trigger':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'email':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'sms':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'delay':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
        );
      case 'exit':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3" />
          </svg>
        );
    }
  };

  const getElementStyle = (type: string) => {
    switch (type) {
      case 'trigger':
        return 'bg-white text-gray-800 border-blue-500 border-2';
      case 'email':
        return 'bg-white text-gray-700 border-blue-500 border-2 border-dashed';
      case 'sms':
        return 'bg-white text-gray-700 border-purple-500 border-2 border-dashed';
      case 'push':
        return 'bg-white text-gray-700 border-orange-500 border-2 border-dashed';
      case 'delay':
        return 'bg-white text-gray-700 border-yellow-500 border-2 border-dashed';
      case 'tag':
        return 'bg-white text-gray-700 border-pink-500 border-2 border-dashed';
      case 'split':
        return 'bg-white text-gray-700 border-indigo-500 border-2 border-dashed';
      case 'ab_test':
        return 'bg-white text-gray-700 border-teal-500 border-2 border-dashed';
      case 'exit':
        return 'bg-gray-500 text-white border-gray-500';
      default:
        return 'bg-white text-gray-700 border-gray-400 border-2 border-dashed';
    }
  };

  const renderElement = (element: WorkflowElement) => {
    const isSelected = selectedElement?.id === element.id;
    const canDelete = element.type !== 'trigger' && element.type !== 'exit';

    return (
      <div
        key={element.id}
        className={"absolute cursor-pointer transition-all duration-200"}
        style={{ left: element.x, top: element.y }}
        onClick={() => handleElementClick(element)}
      >
        <div className={`${getElementStyle(element.type)} rounded-lg p-4 shadow-lg min-w-48 max-w-64 relative group`}>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {getElementIcon(element.type)}
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-sm">{element.title}</h3>
              {element.description && (
                <>
                  <div className="my-2 h-px bg-gray-200" />
                  <p className="text-xs opacity-90 whitespace-pre-line">{element.description}</p>
                </>
              )}
              {element.type === 'trigger' && !(element.config && (element.config.event || element.config.type)) && (
                <p className="text-[11px] text-red-500 mt-1">Trigger must be defined</p>
              )}
            </div>
            {canDelete && (
              <button
                onClick={(e) => handleContextMenu(element.id, e)}
                className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                title="More options"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(backToAutomationsPath)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          {/* Simple editor shortcut — shown when editing an existing automation outside template mode */}
          {automationId && !isTemplateMode && (
            <button
              onClick={() => {
                const base = effectiveWorkspaceId
                  ? `/workspace/${effectiveWorkspaceId}/automations/create`
                  : '/automations/create';
                const params = new URLSearchParams({ automationId });
                navigate(`${base}?${params.toString()}`);
              }}
              className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-800 dark:text-neutral-400 dark:hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h10M4 14h6" />
              </svg>
              Simple editor
            </button>
          )}
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <h1 className="text-lg font-semibold text-gray-900">{automationName}</h1>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">Edit</button>
          </div>
        </div>
          <div className="flex items-center space-x-3">
          {/* Canvas / Activity view toggle */}
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setEditorView('canvas')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                editorView === 'canvas'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Canvas
            </button>
            <button
              onClick={() => setEditorView('activity')}
              className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                editorView === 'activity'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Activity
              {executions.filter((e: any) => e.status === 'failed').length > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                  editorView === 'activity' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-600'
                }`}>
                  {executions.filter((e: any) => e.status === 'failed').length}
                </span>
              )}
            </button>
          </div>
          {!isTemplateMode && (
            <button
              onClick={handleSaveAutomation}
              disabled={loading}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          )}
          <button
            onClick={handleSaveAndClose}
            disabled={loading}
            className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium"
          >
            {loading ? 'Saving...' : (isTemplateMode ? '💾 Save as Template' : 'Save & Close')}
          </button>
          <button 
            onClick={() => setWorkflowElements(autoLayoutNodes(workflowElements))}
            className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            title="Auto-align all nodes vertically"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span className="text-sm font-medium">Re-align Nodes</span>
          </button>
          {/* Test Run button — only shown when automation is saved */}
          {automationId && !isTemplateMode && (
            <button
              onClick={() => setShowTestRunModal(true)}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm font-medium text-sm"
              title="Test the full automation with a specific contact"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Test run
            </button>
          )}
          <button 
            onClick={handleStartStop} 
            className={`px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm font-medium ${
              automationStatus === 'active' 
                ? 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400' 
                : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
          >
            {automationStatus === 'active' ? 'Pause workflow' : 'Start workflow'}
          </button>
        </div>
      </div>

      {/* Auto-pause warning banner */}
      {autoPauseReason && (
        <div className="flex-shrink-0 flex items-start gap-3 bg-red-50 border-b border-red-200 px-6 py-3">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-700">This automation was automatically paused</p>
            <p className="text-xs text-red-600 mt-0.5">{autoPauseReason}</p>
          </div>
          <button
            onClick={() => setAutoPauseReason(null)}
            className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Activity Log view */}
        {editorView === 'activity' && automationId ? (
          <div className="flex-1 overflow-hidden">
            <AutomationActivityLog
              automationId={automationId}
              workspaceId={effectiveWorkspaceId}
              onExecutionsChange={setExecutions}
            />
          </div>
        ) : editorView === 'activity' ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
            Save the automation first to view execution history.
          </div>
        ) : null}

        {/* Canvas view — hidden (not unmounted) when showing activity */}
        <div className={`flex-1 flex overflow-hidden ${editorView !== 'canvas' ? 'hidden' : ''}`}>
        {/* Left Toolbox */}
        <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
          <div className="space-y-6">
            {/* Messages Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">MESSAGES</h3>
              <div className="space-y-2">
                {toolboxItems.slice(0, 1).map((item) => (
                  <div
                    key={item.type}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-move hover:bg-gray-100 transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-gray-600 group-hover:text-gray-800">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{item.title}</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            {/* Flow Actions Section */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">FLOW ACTIONS</h3>
              <div className="space-y-2">
                {toolboxItems.slice(1).map((item) => (
                  <div
                    key={item.type}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-move hover:bg-gray-100 transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-gray-600 group-hover:text-gray-800">
                        {item.icon}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{item.title}</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Central Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <div
            ref={canvasRef}
            className="w-full h-full relative overflow-auto"
            style={{
              backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {dropHint && (
              <div
                className="absolute -mt-5 -ml-5 w-6 h-6 rounded-full bg-white border-2 border-emerald-500 text-emerald-500 flex items-center justify-center shadow"
                style={{ left: dropHint.x, top: dropHint.y }}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            )}
            {validationErrors.length > 0 && (
              <div className="absolute top-4 right-4 bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 text-sm max-w-md z-10">
                <div className="font-medium mb-1">Please fix the following before starting:</div>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            {workflowElements.map(renderElement)}

            {/* Connection Lines */}
            {workflowElements.map((element, index) => {
              // Helper function to estimate node height based on content
              const getNodeHeight = (el: WorkflowElement) => {
                // Base height for padding and icon/title row
                let height = 60; // ~4rem padding + icon row
                
                // Add height for description if present
                if (el.description) {
                  const lines = Math.ceil(el.description.length / 40); // Rough estimate
                  height += 20 + (lines * 16); // Divider + text lines
                }
                
                // Add height for validation message if trigger not configured
                if (el.type === 'trigger' && !(el.config && (el.config.event || el.config.type))) {
                  height += 20;
                }
                
                return height;
              };

              // Split branching visuals
              if (element.type === 'split' || element.type === 'ab_test') {
                const nodeHeight = getNodeHeight(element);
                const yesCenterX = element.x + 96;
                const startY = element.y + nodeHeight;
                const branchDepth = 220; // distance to exits
                const yesExitY = element.y + branchDepth;
                const noOffsetX = 280; // horizontal offset for NO branch
                const noCenterX = element.x + noOffsetX + 96;
                const labelOffset = 70;

                return (
                  <React.Fragment key={`split-lines-${element.id}`}>
                    {/* YES vertical line */}
                    <svg
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 1 }}
                    >
                      <line
                        x1={yesCenterX}
                        y1={startY}
                        x2={yesCenterX}
                        y2={yesExitY - 40}
                        stroke="#94a3b8"
                        strokeWidth="2"
                      />
                    </svg>

                    {/* NO curved then vertical line */}
                    <svg
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 1 }}
                    >
                      <path
                        d={`M ${yesCenterX} ${startY} C ${yesCenterX + 120} ${startY}, ${noCenterX - 120} ${startY + 80}, ${noCenterX} ${startY + 80}`}
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="2"
                      />
                      <line
                        x1={noCenterX}
                        y1={startY + 80}
                        x2={noCenterX}
                        y2={yesExitY - 40}
                        stroke="#94a3b8"
                        strokeWidth="2"
                      />
                    </svg>

                    {/* YES/NO (or A/B) badges */}
                    <div
                      className="absolute w-8 h-8 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center"
                      style={{ left: yesCenterX - 16, top: startY + labelOffset }}
                    >
                      {element.type === 'ab_test' ? 'A' : 'YES'}
                    </div>
                    <div
                      className="absolute w-8 h-8 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center"
                      style={{ left: noCenterX - 16, top: startY + labelOffset + 40 }}
                    >
                      {element.type === 'ab_test' ? 'B' : 'NO'}
                    </div>

                    {/* YES Exit pill */}
                    <div
                      className="absolute px-3 py-1 bg-gray-600 text-white rounded-lg shadow text-sm flex items-center space-x-2"
                      style={{ left: yesCenterX - 24, top: yesExitY }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Exit</span>
                    </div>

                    {/* NO Exit pill */}
                    <div
                      className="absolute px-3 py-1 bg-gray-600 text-white rounded-lg shadow text-sm flex items-center space-x-2"
                      style={{ left: noCenterX - 24, top: yesExitY }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Exit</span>
                    </div>
                  </React.Fragment>
                );
              }
              // Connect trigger to first action element
              if (element.type === 'trigger') {
                const actionElements = workflowElements
                  .filter(el => el.type !== 'trigger' && el.type !== 'exit')
                  .sort((a, b) => a.y - b.y);

                if (actionElements.length > 0) {
                  const firstAction = actionElements[0];
                  const currentHeight = getNodeHeight(element);
                  const centerX = element.x + 96; // Center of 192px width node
                  const startY = element.y + currentHeight; // Bottom of trigger node
                  const endY = firstAction.y; // Top of first action node
                  const endCenterX = firstAction.x + 96;

                  return (
                    <svg
                      key={`connection-trigger-${element.id}`}
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 1, width: '100%', height: '100%' }}
                    >
                      <line
                        x1={centerX}
                        y1={startY}
                        x2={endCenterX}
                        y2={endY}
                        stroke="#94a3b8"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />
                      {/* Arrow at the end */}
                      <polygon
                        points={`${endCenterX},${endY} ${endCenterX - 4},${endY - 8} ${endCenterX + 4},${endY - 8}`}
                        fill="#94a3b8"
                      />
                    </svg>
                  );
                } else {
                  // Connect directly to exit if no actions
                  const exitElement = workflowElements.find(e => e.type === 'exit');
                  if (exitElement) {
                    const currentHeight = getNodeHeight(element);
                    const centerX = element.x + 96;
                    const startY = element.y + currentHeight;
                    const endY = exitElement.y;
                    const endCenterX = exitElement.x + 96;

                    return (
                      <svg
                        key={`connection-trigger-exit-${element.id}`}
                        className="absolute inset-0 pointer-events-none"
                        style={{ zIndex: 1, width: '100%', height: '100%' }}
                      >
                        <line
                          x1={centerX}
                          y1={startY}
                          x2={endCenterX}
                          y2={endY}
                          stroke="#94a3b8"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                        />
                        {/* Arrow at the end */}
                        <polygon
                          points={`${endCenterX},${endY} ${endCenterX - 4},${endY - 8} ${endCenterX + 4},${endY - 8}`}
                          fill="#94a3b8"
                        />
                      </svg>
                    );
                  }
                }
              }

              // Connect action elements in sequence
              const actionElements = workflowElements
                .filter(el => el.type !== 'trigger' && el.type !== 'exit')
                .sort((a, b) => a.y - b.y);
              // If split or ab_test exists, skip linear last-to-exit connection (handled above)
              const hasSplit = actionElements.some(a => a.type === 'split' || a.type === 'ab_test');

              const currentIndex = actionElements.findIndex(el => el.id === element.id);
              if (currentIndex >= 0 && currentIndex < actionElements.length - 1) {
                const nextElement = actionElements[currentIndex + 1];
                const currentHeight = getNodeHeight(element);
                const centerX = element.x + 96;
                const startY = element.y + currentHeight; // Bottom of current node
                const endY = nextElement.y; // Top of next node
                const endCenterX = nextElement.x + 96;

                return (
                  <svg
                    key={`connection-${element.id}-${nextElement.id}`}
                    className="absolute inset-0 pointer-events-none"
                    style={{ zIndex: 1, width: '100%', height: '100%' }}
                  >
                    <line
                      x1={centerX}
                      y1={startY}
                      x2={endCenterX}
                      y2={endY}
                      stroke="#94a3b8"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                    />
                    {/* Arrow at the end */}
                    <polygon
                      points={`${endCenterX},${endY} ${endCenterX - 4},${endY - 8} ${endCenterX + 4},${endY - 8}`}
                      fill="#94a3b8"
                    />
                  </svg>
                );
              }

              // Connect last action to exit
              if (!hasSplit && actionElements.length > 0 && element.id === actionElements[actionElements.length - 1].id) {
                const exitElement = workflowElements.find(e => e.type === 'exit');
                if (exitElement) {
                  const currentHeight = getNodeHeight(element);
                  const centerX = element.x + 96;
                  const startY = element.y + currentHeight;
                  const endY = exitElement.y;
                  const endCenterX = exitElement.x + 96;

                  return (
                    <svg
                      key={`connection-last-exit-${element.id}`}
                      className="absolute inset-0 pointer-events-none"
                      style={{ zIndex: 1, width: '100%', height: '100%' }}
                    >
                      <line
                        x1={centerX}
                        y1={startY}
                        x2={endCenterX}
                        y2={endY}
                        stroke="#94a3b8"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />
                      {/* Arrow at the end */}
                      <polygon
                        points={`${endCenterX},${endY} ${endCenterX - 4},${endY - 8} ${endCenterX + 4},${endY - 8}`}
                        fill="#94a3b8"
                      />
                    </svg>
                  );
                }
              }

              return null;
            })}
          </div>
        </div>
        </div>{/* end canvas wrapper */}

        {/* Right Properties Panel */}
        <div className="w-96 bg-white border-l border-gray-200 p-6 overflow-y-auto">
          {/* Header */}
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {selectedElement?.type === 'delay' ? 'Delay' : selectedElement?.type === 'email' ? 'Email' : selectedElement?.type === 'trigger' || !selectedElement ? 'Edit trigger' : `Edit ${selectedElement.type}`}
          </h2>

          {/* Trigger properties - Omnisend-like */}
          {selectedElement?.type === 'trigger' || !selectedElement ? (
            <>
              <div className="space-y-6">
                {/* TRIGGER */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">TRIGGER</h3>
                  <select
                    value={triggerEvent}
                    onChange={(e) => setTriggerEvent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Please select event</option>
                    {TRIGGER_EVENTS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowTriggerSelectionModal(true)}
                    className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 transition"
                  >
                    Browse trigger library
                  </button>
                  {!triggerEvent && (
                    <p className="mt-2 text-xs text-red-500">Please choose an event to start this automation.</p>
                  )}
                </div>

                {/* OPTIONS */}
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">OPTIONS</h3>

                  {/* Contact filter */}
                  <div className="border border-gray-200 rounded-lg">
                    <div className="px-4 py-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Contact filter</span>
                        {contactFilterType && (
                          <button onClick={() => { setContactFilterType(''); setContactFilterValue(''); setContactFilterNumber(30); setContactFilterGender(''); }} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
                        )}
                      </div>
                      <select
                        value={contactFilterType}
                        onChange={(e) => setContactFilterType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">No filter (applies to everyone)</option>
                        <option value="segment">Segment</option>
                        <option value="tag">Tag</option>
                        <option value="date_added">Date added</option>
                        <option value="first_name">First Name</option>
                        <option value="last_name">Last Name</option>
                        <option value="gender">Gender</option>
                        <option value="country">Country</option>
                        <option value="state">State</option>
                        <option value="city">City</option>
                        <option value="postal_code">Postal Code</option>
                      </select>

                      {/* Dynamic filter value inputs */}
                      {contactFilterType && contactFilterType !== 'date_added' && contactFilterType !== 'gender' && (
                        <input
                          type="text"
                          value={contactFilterValue}
                          onChange={(e) => setContactFilterValue(e.target.value)}
                          placeholder={`Enter ${contactFilterType.replace('_', ' ')}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}

                      {contactFilterType === 'date_added' && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Added in the last</span>
                          <input
                            type="number"
                            min={0}
                            value={contactFilterNumber}
                            onChange={(e) => setContactFilterNumber(Math.max(0, Number(e.target.value)))}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <span className="text-sm text-gray-600">days</span>
                        </div>
                      )}

                      {contactFilterType === 'gender' && (
                        <select
                          value={contactFilterGender}
                          onChange={(e) => setContactFilterGender(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select gender</option>
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Trigger Filters - E-commerce filters, product details, etc. */}
                  {triggerEvent && getAvailableFieldsForTrigger(triggerEvent).length > 0 && (
                    <div className="border border-gray-200 rounded-lg">
                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Trigger filters {triggerFilters.length > 0 ? `(${triggerFilters.length})` : ''}
                          </span>
                          {triggerFilters.length > 0 && (
                            <button
                              onClick={() => setTriggerFilters([])}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-4">
                          Add conditions to filter who enters this automation based on event details (e.g., product price, category, cart value).
                        </p>
                        <TriggerFilterBuilder
                          filters={triggerFilters}
                          onChange={setTriggerFilters}
                          availableFields={getAvailableFieldsForTrigger(triggerEvent)}
                          triggerType={triggerEvent}
                        />
                      </div>
                    </div>
                  )}

                  {/* Exit conditions */}
                  <div className="border border-gray-200 rounded-lg">
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Exit conditions {exitConditions.length > 0 ? `(${exitConditions.length})` : ''}</span>
                      </div>
                      {exitConditions.map((cond, idx) => (
                        <div key={cond.id} className="mb-5">
                          <div className="text-xs text-gray-500 mb-2">Condition {idx + 1}</div>
                          <div className="flex items-center space-x-2">
                            <select
                              value={cond.event}
                              onChange={(e) => setExitConditions(prev => prev.map(c => c.id === cond.id ? { ...c, event: e.target.value } : c))}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select event</option>
                              {EXIT_EVENTS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => setExitConditions(prev => prev.filter(c => c.id !== cond.id))}
                              className="p-2 text-gray-400 hover:text-gray-600"
                              title="Remove"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          {idx < exitConditions.length - 1 && (
                            <div className="flex items-center my-3">
                              <span className="text-xs text-gray-400 mr-2">or</span>
                              <div className="flex-1 h-px bg-gray-200"></div>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => setExitConditions(prev => [...prev, { id: `ec-${Date.now()}`, event: '' }])}
                        className="inline-flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                      >
                        <span>+ Add exit condition</span>
                      </button>
                    </div>
                  </div>

                  {/* Workflow channel settings (static) */}
                  <div className="border border-gray-200 rounded-lg">
                    <button className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Workflow channel settings</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">NEW</span>
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className="px-4 pb-3">
                      <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">Customise</button>
                    </div>
                  </div>

                  {/* Frequency */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Frequency</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Skip contacts who have already been in this automation</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only" checked={triggerSkipAlreadyIn} onChange={(e) => setTriggerSkipAlreadyIn(e.target.checked)} />
                          <div className={`w-10 h-6 rounded-full shadow-inner ${triggerSkipAlreadyIn ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                          <div className={`absolute top-1 ${triggerSkipAlreadyIn ? 'right-1' : 'left-1'} w-4 h-4 bg-white rounded-full shadow transform transition-all`}></div>
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={triggerWindowText}
                          onChange={(e) => setTriggerWindowText(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button className="p-2 text-gray-400 hover:text-gray-600" title="Pick date">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Skip contacts who are currently in or have recently completed other automations</span>
                        <button
                          onClick={() => setTriggerSkipOtherAutomations((v) => !v)}
                          className={`h-6 w-6 rounded-full border flex items-center justify-center ${triggerSkipOtherAutomations ? 'text-gray-600 border-gray-300' : 'text-gray-400 border-gray-300'}`}
                          title="Toggle"
                        >
                          {triggerSkipOtherAutomations ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="9" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </>
          ) : null}

          {/* Split properties */}
          {selectedElement?.type === 'split' ? (
            <>
              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Define your split condition</h3>
                    <p className="text-xs text-gray-500 mb-4">
                      Contacts who match these filters will go down the YES branch. Everyone else will follow the NO branch.
                    </p>
                    <TriggerFilterBuilder
                      filters={splitFilters}
                      onChange={setSplitFilters}
                      availableFields={SPLIT_AVAILABLE_FIELDS}
                      triggerType="split"
                    />
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">When conditions are met (YES branch)</label>
                      <select
                        value={splitYesPath}
                        onChange={(e) => setSplitYesPath(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="__end__">Stop automation</option>
                        {branchTargets
                          .filter(target => target.id !== selectedElement.id)
                          .map(target => (
                            <option key={target.id} value={target.id}>
                              {target.title}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">When conditions are not met (NO branch)</label>
                      <select
                        value={splitNoPath}
                        onChange={(e) => setSplitNoPath(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="__end__">Stop automation</option>
                        {branchTargets
                          .filter(target => target.id !== selectedElement.id)
                          .map(target => (
                            <option key={target.id} value={target.id}>
                              {target.title}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 left-0 right-0 bg-white pt-4 border-t border-gray-200 flex items-center justify-between mt-8">
                <button
                  onClick={() => handleDeleteElement(selectedElement.id)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
                <div className="space-x-3">
                  <button onClick={handleCancelEdit} className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors">Cancel</button>
                  <button onClick={handleUpdateSelected} className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">Update</button>
                </div>
              </div>
            </>
          ) : null}

          {/* Email properties */}
          {selectedElement?.type === 'email' ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {emailName || emailSubject || 'Email action'}
                    </h3>
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      {emailSubject ? (
                        <p>
                          <span className="font-medium text-gray-800">Subject:</span> {emailSubject}
                        </p>
                      ) : (
                        <p className="text-red-500">Subject missing</p>
                      )}
                      {emailTemplateName ? (
                        <p>
                          <span className="font-medium text-gray-800">Template:</span> {emailTemplateName}
                        </p>
                      ) : (
                        <p className="text-red-500">Template not selected</p>
                      )}
                      {emailFromEmail ? (
                        <p>
                          <span className="font-medium text-gray-800">From:</span> {emailFromName || '—'} &lt;{emailFromEmail}&gt;
                        </p>
                      ) : (
                        <p className="text-red-500">From email required</p>
                      )}
                      <p>
                        <span className="font-medium text-gray-800">Timing:</span>{' '}
                        {emailSendMode === 'delay'
                          ? `After ${emailDelayAmount} ${emailDelayUnit}`
                          : emailSendMode === 'specific_time'
                            ? `At ${emailSendTime} (${emailSendTimezone})`
                            : 'Immediately'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEmailEditorModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-400"
                  >
                    <SparklesIcon className="h-4 w-4 text-purple-500" />
                    Edit email settings
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700">
                    {emailTrackOpens ? 'Open tracking on' : 'Open tracking off'}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                    {emailTrackClicks ? 'Click tracking on' : 'Click tracking off'}
                  </span>
                  {emailUtmTracking && (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      UTM auto-tagging enabled
                    </span>
                  )}
                  {emailConditionalSendEnabled && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                      Conditional send
                    </span>
                  )}
                  {emailABTestEnabled && (
                    <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700">
                      Subject A/B test
                    </span>
                  )}
                </div>
              </div>
              {emailTemplateMissing && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Selected template could not be found. Pick another template before publishing this automation.
                </div>
              )}
            </div>
          ) : null}

          {false && showEmailEditorModal && selectedElement?.type === 'email' && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3 py-4 sm:px-4 sm:py-6">
              <div className="flex h-[min(95dvh,900px)] w-full max-w-[min(98vw,1600px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-neutral-900 sm:rounded-3xl sm:h-[95vh]">
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-neutral-700 sm:px-6 sm:py-4">
                  <div className="min-w-0 pr-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">Configure email action</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-neutral-400">
                      Update the content, sending rules, and tracking for this step.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      handleCancelEdit();
                      setShowEmailEditorModal(false);
                    }}
                    className="shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
                  {/* Left side - Scrollable form */}
                  <div className="min-h-0 min-w-0 flex-1 overflow-y-auto border-gray-200 px-4 py-4 dark:border-neutral-700 lg:border-r lg:px-6 lg:py-6">
                    <div className="space-y-8">
                      <section className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-200">
                              Email name
                            </label>
                            <input
                              type="text"
                              value={emailName}
                              onChange={(e) => setEmailName(e.target.value)}
                              placeholder="Welcome email #1"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                              Internal label used to recognise this action inside the automation builder.
                            </p>
                          </div>
                          <div>
                            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <label className="block text-sm font-medium text-gray-700 dark:text-neutral-200">
                                Subject line <span className="text-red-500">*</span>
                              </label>
                              <select
                                className="w-full shrink-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 sm:max-w-[11rem] sm:py-1"
                                onChange={(event) => {
                                  const token = event.target.value;
                                  if (token) {
                                    handleInsertSubjectToken(token);
                                    event.target.selectedIndex = 0;
                                  }
                                }}
                              >
                                <option value="">Insert personalization…</option>
                                {personalizationTokens.map(token => (
                                  <option key={token.value} value={token.value}>{token.label}</option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="text"
                              value={emailSubject}
                              onChange={(e) => setEmailSubject(e.target.value)}
                              placeholder="Welcome aboard, {{first_name}}!"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                              Support for personalization tags like{' '}
                              <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-neutral-700 dark:text-neutral-200">
                                {'{{first_name}}'}
                              </code>
                              .
                            </p>
                          </div>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-200">Preheader</label>
                          <input
                            type="text"
                            value={emailPreheader}
                            onChange={(e) => setEmailPreheader(e.target.value)}
                            placeholder="Short summary that appears next to the subject line"
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                          />
                        </div>
                      </section>

                      <section className="space-y-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-400">
                          Sender details
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-200">From name</label>
                            <input
                              type="text"
                              value={emailFromName}
                              onChange={(e) => setEmailFromName(e.target.value)}
                              placeholder="Your brand"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-200">
                              From email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              list="automation-email-from-options"
                              value={emailFromEmail}
                              onChange={(e) => handleFromEmailChange(e.target.value)}
                              placeholder="hello@yourdomain.com"
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                            <datalist id="automation-email-from-options">
                              {emailFromEmailOptions.map(option => (
                                <option key={`${option.value}-${option.status}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </datalist>
                            {emailFromEmailStatus === 'pending' && (
                              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" />
                                <span>This domain is still pending verification. Emails will fall back to your default sender until verification finishes.</span>
                              </div>
                            )}
                            {emailFromEmailStatus === 'failed' && (
                              <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" />
                                <span>We can’t verify this domain yet. Verify DNS records or choose another sender.</span>
                              </div>
                            )}
                            {emailFromEmailStatus === 'unknown' && (
                              <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                                <InformationCircleIcon className="mt-0.5 h-4 w-4" />
                                <span>We’ll automatically route unverified addresses through your workspace subdomain to protect deliverability.</span>
                              </div>
                            )}
                            {(emailFromEmailStatus === 'verified' || emailFromEmailStatus === 'active') && (
                              <div className="mt-2 flex items-start gap-2 text-sm text-emerald-600">
                                <SparklesIcon className="mt-0.5 h-4 w-4" />
                                <span>Verified sending domain ready to use.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-200">Reply-to email</label>
                          <input
                            type="email"
                            value={emailReplyTo}
                            onChange={(e) => setEmailReplyTo(e.target.value)}
                            placeholder={emailFromEmail || 'reply@yourdomain.com'}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                            Leave blank to reply to the From address.
                          </p>
                        </div>
                      </section>

                      <section className="space-y-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-400">
                          Email content
                        </h3>
                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-800/80">
                          <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 dark:border-neutral-700 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {emailTemplateId ? emailTemplateName || 'Selected template' : 'No template selected yet'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-neutral-400">
                                {emailTemplateId
                                  ? `Template ID: ${emailTemplateId}`
                                  : 'Pick a template below, or use the preview column.'}
                              </p>
                            </div>
                            {emailTemplateId ? (
                              <button
                                type="button"
                                onClick={() => setEmailTemplateModalOpen(true)}
                                className="shrink-0 self-start text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 sm:self-auto"
                              >
                                Change template
                              </button>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-3 px-4 py-3 sm:px-5 sm:py-4">
                            <button
                              type="button"
                              onClick={() => setEmailTemplateModalOpen(true)}
                              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 dark:border-neutral-600 dark:text-neutral-200 dark:hover:border-neutral-500"
                            >
                              <SparklesIcon className="h-4 w-4 text-purple-500" />
                              {emailTemplateId ? 'Choose another template' : 'Pick a template'}
                            </button>
                            <button
                              type="button"
                              onClick={handlePreviewEmail}
                              disabled={!emailTemplateId || emailPreviewLoading}
                              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-600 dark:text-neutral-200 dark:hover:border-neutral-500"
                            >
                              <EyeIcon className="h-4 w-4 text-gray-500" />
                              {emailPreviewLoading ? 'Generating preview…' : 'Preview'}
                            </button>
                            <button
                              type="button"
                              onClick={handleSendTestEmail}
                              disabled={!emailTemplateId || emailTestSending}
                              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-600 dark:text-neutral-200 dark:hover:border-neutral-500"
                            >
                              <PaperAirplaneIcon className="h-4 w-4 text-gray-500" />
                              {emailTestSending ? 'Sending test…' : 'Send test email'}
                            </button>
                          </div>
                        </div>
                        {emailTemplateMissing && (
                          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            Selected template could not be found. Pick another template before publishing this automation.
                          </div>
                        )}
                      </section>

                      <section className="space-y-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Send timing</h3>
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300">
                            <input
                              type="radio"
                              className="h-4 w-4 text-purple-600"
                              checked={emailSendMode === 'immediate'}
                              onChange={() => setEmailSendMode('immediate')}
                            />
                            <span>Send immediately after the previous step</span>
                          </label>
                          <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300">
                            <input
                              type="radio"
                              className="h-4 w-4 text-purple-600"
                              checked={emailSendMode === 'delay'}
                              onChange={() => setEmailSendMode('delay')}
                            />
                            <span>Send after a delay</span>
                          </label>
                          {emailSendMode === 'delay' && (
                            <div className="ml-6 grid gap-3 md:w-2/3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                              <input
                                type="number"
                                min={0}
                                value={emailDelayAmount}
                                onChange={(e) => setEmailDelayAmount(Math.max(0, Number(e.target.value)))}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                              />
                              <select
                                value={emailDelayUnit}
                                onChange={(e) => setEmailDelayUnit(e.target.value as typeof emailDelayUnit)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                              </select>
                            </div>
                          )}
                          <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300">
                            <input
                              type="radio"
                              className="h-4 w-4 text-purple-600"
                              checked={emailSendMode === 'specific_time'}
                              onChange={() => setEmailSendMode('specific_time')}
                            />
                            <span>Send at a specific time of day</span>
                          </label>
                          {emailSendMode === 'specific_time' && (
                            <div className="ml-6 grid gap-3 md:w-2/3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                              <input
                                type="time"
                                value={emailSendTime}
                                onChange={(e) => setEmailSendTime(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                              />
                              <select
                                value={emailSendTimezone}
                                onChange={(e) => setEmailSendTimezone(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                              >
                                {availableTimezones.map(zone => (
                                  <option key={zone} value={zone}>{zone}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </section>

                      <section className="space-y-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tracking & UTM</h3>
                        <label className="flex items-center gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600"
                            checked={emailTrackOpens}
                            onChange={(e) => setEmailTrackOpens(e.target.checked)}
                          />
                          <span>Track email opens</span>
                        </label>
                        <label className="flex items-center gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600"
                            checked={emailTrackClicks}
                            onChange={(e) => setEmailTrackClicks(e.target.checked)}
                          />
                          <span>Track link clicks</span>
                        </label>
                        <label className="flex items-center gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600"
                            checked={emailUtmTracking}
                            onChange={(e) => setEmailUtmTracking(e.target.checked)}
                          />
                          <span>Add UTM parameters to links</span>
                        </label>
                        <p className="text-xs text-gray-500">
                          UTM parameters help attribute visits back to this automation in tools like Google Analytics.
                        </p>
                      </section>

                      <section className="space-y-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Conditional send</h3>
                        <label className="flex items-center gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600"
                            checked={emailConditionalSendEnabled}
                            onChange={(e) => setEmailConditionalSendEnabled(e.target.checked)}
                          />
                          <span>Send only if the contact has a specific tag</span>
                        </label>
                        {emailConditionalSendEnabled && (
                          <div className="grid gap-2 md:w-2/3">
                            <input
                              type="text"
                              value={emailConditionalTag}
                              onChange={(e) => setEmailConditionalTag(e.target.value)}
                              placeholder="Enter tag name or ID"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                            />
                            <p className="text-xs text-gray-500">Contacts without this tag will skip the email.</p>
                          </div>
                        )}
                      </section>

                      <section className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subject line A/B test</h3>
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-purple-600"
                              checked={emailABTestEnabled}
                              onChange={(e) => setEmailABTestEnabled(e.target.checked)}
                            />
                            <span>Enable</span>
                          </label>
                        </div>
                        {emailABTestEnabled && (
                          <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Variant B subject</label>
                              <input
                                type="text"
                                value={emailABSubjectB}
                                onChange={(e) => setEmailABSubjectB(e.target.value)}
                                placeholder="e.g. {{first_name}}, ready for your next adventure?"
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Traffic split</label>
                              <input
                                type="range"
                                min={1}
                                max={99}
                                value={emailABSplit}
                                onChange={(e) => setEmailABSplit(Number(e.target.value))}
                                className="w-full"
                              />
                              <div className="mt-2 flex justify-between text-xs text-gray-500">
                                <span>Variant A: {emailABSplit}%</span>
                                <span>Variant B: {100 - emailABSplit}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </section>

                      <section className="rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4">
                        <div className="flex items-start gap-3">
                          <SparklesIcon className="h-5 w-5 text-indigo-500" />
                          <div>
                            {emailAnalyticsPreview ? (
                              <>
                                <p className="text-sm font-medium text-indigo-900">Projected performance once live</p>
                                <ul className="mt-2 space-y-1 text-xs text-indigo-900/80">
                                  {emailAnalyticsPreview?.projectedOpens !== undefined && (
                                    <li>• Expected opens: ~{emailAnalyticsPreview?.projectedOpens?.toLocaleString()}</li>
                                  )}
                                  {emailAnalyticsPreview?.projectedClicks !== undefined && (
                                    <li>• Expected clicks: ~{emailAnalyticsPreview?.projectedClicks?.toLocaleString()}</li>
                                  )}
                                  {emailAnalyticsPreview?.sampleSize !== undefined && (
                                    <li>• Based on a sample size of {emailAnalyticsPreview?.sampleSize?.toLocaleString()} contacts</li>
                                  )}
                                </ul>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-indigo-900">Analytics preview</p>
                                <p className="mt-1 text-xs text-indigo-900/80">
                                  Once this automation is live we’ll surface open, click, and conversion metrics right here.
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>

                  {/* Right side — live preview (stacked below form on narrow screens) */}
                  <div className="flex min-h-[min(280px,40vh)] w-full shrink-0 flex-col border-t border-gray-200 bg-gray-50 dark:border-neutral-700 dark:bg-neutral-800 lg:min-h-0 lg:w-1/2 lg:border-l lg:border-t-0">
                    {!emailTemplateId ? (
                      <div className="flex flex-1 items-center justify-center p-6 sm:p-8">
                        <div className="max-w-sm space-y-4 text-center">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-700 sm:h-16 sm:w-16">
                            <DocumentTextIcon className="h-7 w-7 text-gray-400 dark:text-neutral-500 sm:h-8 sm:w-8" />
                          </div>
                          <div>
                            <h4 className="mb-1 text-base font-semibold text-gray-900 dark:text-white sm:text-lg">Preview</h4>
                            <p className="text-sm text-gray-500 dark:text-neutral-400">
                              Choose a template on the left to see your design here. This panel updates automatically once a template is selected.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : emailPreviewLoading ? (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center space-y-3">
                          <div className="mx-auto animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Generating preview...</p>
                        </div>
                      </div>
                    ) : emailPreviewHtml ? (
                      <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-neutral-700 px-4 py-3 bg-white dark:bg-neutral-900">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Email preview</h4>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handlePreviewEmail}
                              className="text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                            >
                              Refresh
                            </button>
                            <button
                              type="button"
                              onClick={() => setEmailTemplateModalOpen(true)}
                              className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                            >
                              Change template
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-white dark:bg-neutral-900 p-4">
                          <iframe
                            title="email-preview"
                            className="w-full h-full min-h-[600px] border border-gray-200 rounded-lg dark:border-neutral-700"
                            srcDoc={emailPreviewHtml}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center p-8">
                        <div className="text-center space-y-4">
                          <div className="mx-auto w-16 h-16 rounded-full bg-gray-200 dark:bg-neutral-700 flex items-center justify-center">
                            <EyeIcon className="h-8 w-8 text-gray-400 dark:text-neutral-500" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Preview not available</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                              Click "Preview" to generate a preview of your email
                            </p>
                            <button
                              type="button"
                              onClick={handlePreviewEmail}
                              disabled={emailPreviewLoading}
                              className="inline-flex items-center gap-2 rounded-lg bg-black px-6 py-3 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-purple-600 dark:hover:bg-purple-700"
                            >
                              <EyeIcon className="h-5 w-5" />
                              Generate preview
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 dark:border-neutral-700 dark:bg-neutral-900 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedElement) {
                        handleDeleteElement(selectedElement.id);
                      }
                      setShowEmailEditorModal(false);
                    }}
                    className="order-2 text-left text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 sm:order-1"
                  >
                    Delete action
                  </button>
                  <div className="order-1 flex w-full items-center justify-end gap-2 sm:order-2 sm:w-auto sm:gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        handleCancelEdit();
                        setShowEmailEditorModal(false);
                      }}
                      className="rounded-lg px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleUpdateSelected();
                        setShowEmailEditorModal(false);
                      }}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
                    >
                      Save changes
                    </button>
                  </div>
                </div>
              </div>

              {emailTemplateModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4 py-10">
                  <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                      <h3 className="text-lg font-semibold text-gray-900">Choose an email template</h3>
                      <button
                        type="button"
                        onClick={() => setEmailTemplateModalOpen(false)}
                        className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
                      {(() => {
                        if (!currentUser?.uid) {
                          return (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
                              Sign in to access your saved templates.
                            </div>
                          );
                        }
                        // After the check above, currentUser and currentUser.uid are guaranteed to exist
                        return (
                          <EmailTemplateSelector
                            userId={currentUser!.uid!}
                            selectedTemplateId={emailTemplateId || undefined}
                            onTemplateSelect={handleTemplateSelected}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {emailPreviewModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 py-10">
                  <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                      <h3 className="text-lg font-semibold text-gray-900">Email preview</h3>
                      <button
                        type="button"
                        onClick={() => setEmailPreviewModalOpen(false)}
                        className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="max-h-[75vh] overflow-y-auto bg-gray-50">
                      {emailPreviewLoading ? (
                        <div className="flex items-center justify-center p-10 text-sm text-gray-600">
                          Generating preview…
                        </div>
                      ) : (
                        <iframe
                          title="automation-email-preview"
                          className="h-[75vh] w-full border-0 bg-white"
                          srcDoc={emailPreviewHtml}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Simplified Email Action Modal - New UI */}
          {showEmailEditorModal && selectedElement?.type === 'email' && (
            <SimpleEmailActionModal
              isOpen={showEmailEditorModal}
              onClose={() => {
                setShowEmailEditorModal(false);
                setSelectedElement(null);
              }}
              onSave={(emailConfig: EmailActionConfig) => {
                if (!selectedElement) return;

                // Update the element with the new config
                const updatedElement: WorkflowElement = {
                  ...selectedElement,
                  config: {
                    ...selectedElement.config,
                    emailName: emailConfig.emailName,
                    subject: emailConfig.subject,
                    preheader: emailConfig.preheader,
                    fromName: emailConfig.fromName,
                    fromEmail: emailConfig.fromEmail,
                    emailTemplateId: emailConfig.templateId,
                    emailTemplateName: emailConfig.templateName,
                    templateType: emailConfig.templateType, // Save template type
                  }
                };

                // Update the workflow elements
                setWorkflowElements(workflowElements.map(el =>
                  el.id === selectedElement.id ? updatedElement : el
                ));
                setShowEmailEditorModal(false);
                setSelectedElement(null);
              }}
              initialConfig={{
                emailName: (selectedElement?.config as any)?.emailName || '',
                subject: (selectedElement?.config as any)?.subject || '',
                preheader: (selectedElement?.config as any)?.preheader || '',
                fromName: (selectedElement?.config as any)?.fromName || '',
                fromEmail: (selectedElement?.config as any)?.fromEmail || '',
                templateId: (selectedElement?.config as any)?.emailTemplateId || '',
                templateName: (selectedElement?.config as any)?.emailTemplateName || '',
                templateType: (selectedElement?.config as any)?.templateType || 'user', // Default to 'user' since we only show user templates
              }}
            />
          )}

          {/* AB Test properties */}
          {selectedElement?.type === 'ab_test' ? (
            <>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Traffic allocation</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant A (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={abTrafficA}
                        onChange={(e) => {
                          const a = Math.max(0, Math.min(100, Number(e.target.value)));
                          setAbTrafficA(a);
                          setAbTrafficB(Math.max(0, 100 - a));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant B (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={abTrafficB}
                        onChange={(e) => {
                          const b = Math.max(0, Math.min(100, Number(e.target.value)));
                          setAbTrafficB(b);
                          setAbTrafficA(Math.max(0, 100 - b));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">A + B must equal 100%.</p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Goal metric</h3>
                  <select
                    value={abGoal}
                    onChange={(e) => setAbGoal(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="open_rate">Open rate</option>
                    <option value="click_rate">Click rate</option>
                    <option value="purchase">Purchase</option>
                    <option value="revenue">Revenue</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evaluation window (hours)</label>
                    <input
                      type="number"
                      min={1}
                      value={abWindowHours}
                      onChange={(e) => setAbWindowHours(Math.max(1, Number(e.target.value)))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex items-center space-x-3 mt-6">
                    <input
                      id="autoWinner"
                      type="checkbox"
                      checked={abAutoWinner}
                      onChange={(e) => setAbAutoWinner(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="autoWinner" className="text-sm text-gray-700">Select winner automatically</label>
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="sticky bottom-0 left-0 right-0 bg-white pt-4 border-t border-gray-200 flex items-center justify-between mt-8">
                <div />
                <div className="space-x-3">
                  <button onClick={handleCancelEdit} className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors">Cancel</button>
                  <button onClick={handleUpdateSelected} className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">Update</button>
                </div>
              </div>
            </>
          ) : null}

          {/* Delay properties - Omnisend-like */}
          {selectedElement?.type === 'delay' ? (
            <>
              <div className="mb-6">
                <p className="text-sm text-gray-600">Choose the time delay between the last and the next workflow steps.</p>
              </div>

              {/* Wait for */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Wait for</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min={0}
                    value={delayAmount}
                    onChange={(e) => setDelayAmount(Math.max(0, Number(e.target.value)))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <select
                    value={delayUnit}
                    onChange={(e) => setDelayUnit(e.target.value as any)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                  </select>
                </div>
              </div>

              {/* Selected days only */}
              <label className="flex items-center space-x-3 text-sm text-gray-700 mb-8">
                <input
                  type="checkbox"
                  checked={delaySelectedDaysOnly}
                  onChange={(e) => setDelaySelectedDaysOnly(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                />
                <span>Send on the selected day(s) of the week</span>
              </label>

              {/* Footer actions */}
              <div className="sticky bottom-0 left-0 right-0 bg-white pt-4 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => handleDeleteElement(selectedElement.id)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
                <div className="space-x-3">
                  <button onClick={handleCancelEdit} className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateSelected}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Update
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
        <div className="flex items-center space-x-3">
          <button className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors">
            Cancel
          </button>
          <button className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
            Update
          </button>
        </div>
      </div>

      {/* Context Menu */}
      {showTriggerSelectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-8 py-10">
          <div className="w-full max-w-7xl h-[90vh] bg-white dark:bg-neutral-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-neutral-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Choose a trigger</h2>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-3xl">
                    A trigger decides how contacts enter this automation. Pick the event that should start your workflow so every step runs at the right moment.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTriggerSelectionModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
                  aria-label="Close trigger selection"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 px-6 py-6 overflow-hidden">
              <div className="flex gap-6 h-full overflow-hidden">
                <nav className="w-60 flex-shrink-0 border-r border-gray-200 dark:border-neutral-800 pr-4 h-full overflow-y-auto">
                  <div className="space-y-2">
                    {TRIGGER_CATEGORIES.map((category) => {
                      const isActive = category.id === activeTriggerCategoryId;
                      return (
                        <button
                          type="button"
                          key={category.id}
                          onClick={() => setActiveTriggerCategoryId(category.id)}
                          className={`w-full text-left px-3 py-3 rounded-lg transition ${isActive
                            ? 'bg-blue-50 text-blue-700 font-semibold dark:bg-blue-500/20 dark:text-blue-100'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800'
                            }`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md border ${isActive
                                ? 'border-blue-500 bg-blue-100 text-blue-600 dark:border-blue-400 dark:bg-blue-500/30 dark:text-blue-100'
                                : 'border-gray-200 text-gray-500 dark:border-neutral-700 dark:text-gray-300'
                                }`}
                            >
                              {category.icon}
                            </span>
                            <div>
                              <div className="text-sm font-medium">{category.title}</div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {category.options.length} trigger{category.options.length > 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </nav>
                <div className="flex-1 h-full overflow-y-auto pr-1">
                  {activeTriggerCategory ? (
                    <section>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{activeTriggerCategory.title}</h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {activeTriggerCategory.description}
                      </p>
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        {activeTriggerCategory.options.map((option) => (
                          <div
                            key={option.value}
                            onClick={(e) => {
                              if (!option.requiresStoreConnection || hasConnectedStore) {
                                handleTriggerSelection(option);
                              }
                            }}
                            className={`w-full text-left border rounded-2xl px-5 py-5 transition ${option.requiresStoreConnection && !hasConnectedStore
                              ? 'border-gray-200 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-800/30 opacity-90'
                              : 'border-gray-200 dark:border-neutral-700 hover:border-blue-500 hover:ring-2 hover:ring-blue-100 dark:hover:ring-blue-500/30 cursor-pointer'
                              }`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="inline-flex items-center justify-center text-blue-600 dark:text-blue-400">
                                {option.icon ?? activeTriggerCategory.icon}
                              </span>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {option.label}
                              </div>
                            </div>
                            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                              {option.description}
                            </p>
                            {option.requiresStoreConnection && !hasConnectedStore && (
                              <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                                Need to connect your store.{' '}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const integrationPath = effectiveWorkspaceId
                                      ? `/workspace/${effectiveWorkspaceId}/integration`
                                      : '/integration';
                                    window.open(integrationPath, '_blank', 'noopener,noreferrer');
                                  }}
                                  className="font-semibold underline text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition cursor-pointer"
                                >
                                  Connect it now
                                </button>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                      No triggers found for this category.
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {contextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-32"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => handleDeleteElement(contextMenu.elementId)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Delete
          </button>
          <div className="border-t border-gray-200 my-1"></div>
          <button
            onClick={() => handleDuplicateElement(contextMenu.elementId)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Duplicate
          </button>
        </div>
      )}

      {/* Template Metadata Modal */}
      {showTemplateMetadataModal && (
        <TemplateMetadataModal
          isOpen={showTemplateMetadataModal}
          onClose={() => setShowTemplateMetadataModal(false)}
          onSave={(metadata) => {
            setShowTemplateMetadataModal(false);
            handleSaveAsTemplate(metadata);
          }}
        />
      )}

      {/* Test Run Modal */}
      {showTestRunModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-950 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 pt-6 pb-5 border-b border-slate-100 dark:border-neutral-800 flex items-center justify-between">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white">Test automation</h2>
                <p className="text-xs text-slate-500 mt-0.5">Runs the full flow for a specific contact. Real emails will be sent.</p>
              </div>
              <button onClick={() => setShowTestRunModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 dark:text-neutral-400">Contact email</label>
                <input
                  type="email"
                  value={testContactEmail}
                  onChange={(e) => setTestContactEmail(e.target.value)}
                  placeholder={currentUser?.email || 'contact@example.com'}
                  className="w-full h-9 rounded-xl bg-slate-100 dark:bg-neutral-800 border-0 px-3 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:focus:ring-white/10"
                />
                <p className="text-[11px] text-slate-400 dark:text-neutral-600">The contact must already exist in this workspace.</p>
              </div>
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 px-3 py-2.5">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  ⚠️ This will execute the automation for real — emails will be sent and tags will be applied.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end gap-2">
              <button onClick={() => setShowTestRunModal(false)} className="px-4 py-2 text-[13px] font-medium text-slate-600 dark:text-neutral-300 bg-slate-100 dark:bg-neutral-800 rounded-full hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleTestRun}
                disabled={testRunLoading}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white dark:text-slate-900 bg-slate-900 dark:bg-white rounded-full hover:bg-black dark:hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {testRunLoading ? (
                  <><div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> Running…</>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /></svg>
                    Run test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Template Metadata Modal Component
interface TemplateMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (metadata: Partial<AutomationTemplate>) => void;
}

const TemplateMetadataModal: React.FC<TemplateMetadataModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AutomationTemplate['category']>('ecommerce');
  const [type, setType] = useState('');
  const [goal, setGoal] = useState(''); // Optional
  const [channel, setChannel] = useState<'Email' | 'Email + SMS' | 'SMS' | 'Multi-channel'>('Email'); // Optional
  const [badge, setBadge] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('1 day');
  const [isRecommended, setIsRecommended] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      description,
      category,
      type,
      goal,
      channel,
      badge,
      estimatedDuration,
      isRecommended,
      isActive,
      isPublic,
      tags: [],
      isPremium: false,
      price: 0,
      difficulty: 'beginner'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Template Details
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Add information about this automation template
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., Welcome New Subscribers"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={3}
                placeholder="Brief description of what this template does..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Category & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as AutomationTemplate['category'])}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="ecommerce">🛒 E-commerce</option>
                  <option value="promotions">🎉 Promotions</option>
                  <option value="events">📅 Events</option>
                  <option value="special_occasions">🎁 Special Occasions</option>
                  <option value="lead_engagement">🎯 Lead Engagement</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Subtitle
                </label>
                <input
                  type="text"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  placeholder="e.g., Welcome series, Abandoned cart recovery"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Optional: A short subtitle or tagline</p>
              </div>
            </div>

            {/* Badge & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Badge (Optional)
                </label>
                <input
                  type="text"
                  value={badge}
                  onChange={(e) => setBadge(e.target.value)}
                  placeholder="e.g., Popular, High Order Rate"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estimated Duration
                </label>
                <input
                  type="text"
                  value={estimatedDuration}
                  onChange={(e) => setEstimatedDuration(e.target.value)}
                  placeholder="e.g., 3 days, 1 week"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="flex flex-wrap gap-4 pt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isRecommended}
                  onChange={(e) => setIsRecommended(e.target.checked)}
                  className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">⭐ Recommended</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">✓ Active</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">👁️ Public</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || !description.trim()}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              💾 Save Template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationEditor;

export { };
